import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAssistantDashboard } from "../hooks/useAssistantDashboard";
import { assistantService } from "../services/assistantService";
import {
  CASE_STAGE_BADGE_BY_STATUS,
  SOURCE_OPTIONS,
} from "../constants/dashboard.constants";
import {
  currentDateTimeLocalInputValue,
  dateTimeLocalToIso,
} from "../utils/dateTime";
import {
  AssistantAssignedTaskItem,
  AssistantCaseCurrentStageDocumentsResponse,
  AssistantCaseListItem,
  AssistantCaseTaskItem,
  LeadCurrentStageDocumentsResponse,
  LeadDocumentReviewAction,
  AssistantLeadTaskItem,
  LeadBookingDraft,
  LeadListItem,
  LeadStageDocumentFileItem,
  LeadStageDocumentItem,
} from "../types/assistant.types";
import { WorkflowTemplateResponse } from "../types/workflowTemplate.types";
import { deriveCaseWorkflow, normalizeCaseStageToken } from "../utils/caseWorkflow";
import { deriveLeadWorkflow } from "../utils/leadWorkflow";
import { isValidEmail } from "../utils/validation";
import UserMenu from "../ui/components/UserMenu";

const statusToBadgeClass = (status: string) => {
  const normalized = status.trim().toLowerCase();
  if (!normalized) return "badge-amber";
  if (normalized === "merged") return "badge-gray";
  if (normalized === "converted") return "badge-green";
  if (
    normalized.includes("ineligible") ||
    normalized.includes("disqualified") ||
    normalized.includes("decline")
  ) {
    return "badge-red";
  }
  if (
    normalized.includes("eligibility") ||
    normalized.includes("engagement") ||
    normalized.includes("engaged")
  ) {
    return "badge-green";
  }
  if (
    normalized.includes("consultation") ||
    normalized === "contacted"
  ) {
    return "badge-blue";
  }
  if (normalized.includes("lead_capture") || normalized.includes("lead_captured")) {
    return "badge-amber";
  }
  return "badge-amber";
};

const formatDate = (value: string | null) => {
  if (!value) return "Not scheduled";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const formatDateTime = (value: string | null) => {
  if (!value) return "No due date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatConsultationSchedule = (value: string | null) => {
  if (!value) return "Not scheduled";
  return value.includes("T") ? formatDateTime(value) : `${formatDate(value)} at 2:00 PM`;
};

const taskSourceBadgeClass = (source: string) => (source === "CASE" ? "badge-purple" : "badge-blue");

const toErrorMessage = (error: unknown, fallback: string) => {
  if (typeof error === "object" && error !== null && "response" in error) {
    const response = (error as { response?: { data?: { detail?: string } } }).response;
    if (response?.data?.detail) return response.data.detail;
  }
  return fallback;
};

const readDetailsString = (details: Record<string, unknown>, keys: string[]): string | null => {
  for (const key of keys) {
    const value = details[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return null;
};

const NO_SHOW_REBOOK_TASK_CODES = new Set([
  "CONSULTATION_NO_SHOW_FOLLOWUP",
  "CONSULTATION_FOLLOWUP_REBOOK",
]);

const WRONG_NUMBER_TASK_CODES = new Set(["CONSULTATION_WRONG_NUMBER_FOLLOWUP"]);
const ENGAGEMENT_SIGNED_DOCUMENT_TASK_CODE = "ENGAGEMENT_SIGNED_DOCUMENT_UPLOAD";

const leadTaskCode = (task: AssistantLeadTaskItem): string => {
  const raw = task.details_json?.task_code;
  if (typeof raw !== "string") return "";
  return raw.trim().toUpperCase();
};

const leadTaskFollowupOutcome = (task: AssistantLeadTaskItem): string => {
  const raw = task.details_json?.followup_outcome;
  if (typeof raw !== "string") return "";
  return raw.trim().toUpperCase();
};

const isNoShowRebookTask = (task: AssistantLeadTaskItem): boolean => {
  const code = leadTaskCode(task);
  if (NO_SHOW_REBOOK_TASK_CODES.has(code)) return true;
  const outcome = leadTaskFollowupOutcome(task);
  return outcome === "FOLLOWUP" || outcome === "NOT_REACHED";
};

const isWrongNumberTask = (task: AssistantLeadTaskItem): boolean => {
  const code = leadTaskCode(task);
  if (WRONG_NUMBER_TASK_CODES.has(code)) return true;
  return leadTaskFollowupOutcome(task) === "WRONG_NUMBER";
};

const resolveAssignedTaskFullName = (
  task: AssistantAssignedTaskItem,
  leadNameById: Map<string, string>
): string | null => {
  if (task.source === "LEAD") {
    const leadName = leadNameById.get(task.source_id);
    if (leadName) return leadName;
  }

  const details = task.details_json ?? {};
  const fullName = readDetailsString(details, [
    "full_name",
    "lead_full_name",
    "client_full_name",
    "client_name",
    "lead_name",
    "applicant_name",
    "name",
  ]);
  if (fullName) return fullName;

  const firstName = readDetailsString(details, [
    "first_name",
    "lead_first_name",
    "client_first_name",
  ]);
  const lastName = readDetailsString(details, [
    "last_name",
    "lead_last_name",
    "client_last_name",
  ]);
  const composedName = `${firstName ?? ""} ${lastName ?? ""}`.trim();
  return composedName || null;
};

const fullName = (firstName: string, lastName: string) =>
  `${firstName} ${lastName}`.trim();
const workflowTemplateLabel = (template: WorkflowTemplateResponse) => template.name;

type ActiveSelection = "lead" | "case";
type DocumentModalSource = "lead" | "case";
type ClientSignedDocumentStatus = "PENDING" | "RECEIVED" | "NOT_RECEIVED";
type InitialLeadContactOutcome = "CONTACTED" | "NO_ANSWER" | "WRONG_NUMBER";

const ASSISTANT_PERSONA = "assistant";

const isAssistantPersonaStage = (stage: { personas?: string[] }) =>
  Array.isArray(stage.personas) &&
  stage.personas.some((persona) => persona.toLowerCase() === ASSISTANT_PERSONA);

const buildInitialBookingState = (lead: LeadListItem): LeadBookingDraft => ({
  consultationDate: lead.consultation_date ?? "",
  consultationNotes: "",
  qualificationOutcome: "",
});

const toLabel = (value: string) =>
  value
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const DEFAULT_CASE_ACTION = {
  title: "Progress case",
  detail:
    "Complete current-stage tasks and required documents, then move to the next workflow stage.",
};

type WorkflowStepState = "done" | "active" | "locked";

interface WorkflowStep {
  id: string;
  label: string;
  state: WorkflowStepState;
}

const documentStatusLabel = (status: string) => status.replaceAll("_", " ");

const documentStatusBadgeClass = (status: string) => {
  switch (status) {
    case "VERIFIED":
      return "badge-green";
    case "REJECTED":
      return "badge-red";
    case "UPLOADED":
    case "UNDER_REVIEW":
      return "badge-blue";
    case "WAIVED":
      return "badge-purple";
    case "NOT_REQUESTED":
    case "REQUESTED":
    case "EXPIRED":
    default:
      return "badge-gray";
  }
};

const latestDocumentFile = (document: LeadStageDocumentItem): LeadStageDocumentFileItem | null => {
  if (!Array.isArray(document.files) || document.files.length === 0) {
    return null;
  }
  return document.files[document.files.length - 1] ?? null;
};

const ownershipRoles = (
  document: LeadStageDocumentItem,
  key: "submitted_by" | "verified_by"
): string[] => {
  const source = document.source_requirement_json;
  if (!source || typeof source !== "object") return [];
  const rawOwnership = (source as Record<string, unknown>).ownership;
  if (!rawOwnership || typeof rawOwnership !== "object") return [];
  const rawValue = (rawOwnership as Record<string, unknown>)[key];
  if (!Array.isArray(rawValue)) return [];
  return rawValue
    .map((value) => (typeof value === "string" ? value.trim().toLowerCase() : ""))
    .filter((value) => Boolean(value));
};

const assistantCanUploadDocument = (document: LeadStageDocumentItem) =>
  (() => {
    const roles = ownershipRoles(document, "submitted_by");
    return roles.length === 0 || roles.includes("assistant");
  })();

const assistantCanReviewDocument = (document: LeadStageDocumentItem) =>
  (() => {
    const roles = ownershipRoles(document, "verified_by");
    return roles.length === 0 || roles.includes("assistant");
  })();

const hasDocumentUploaded = (document: LeadStageDocumentItem): boolean => {
  if (Array.isArray(document.files) && document.files.length > 0) return true;
  if (document.uploaded_at) return true;
  const normalizedStatus = String(document.status ?? "").toUpperCase();
  return ["UPLOADED", "UNDER_REVIEW", "VERIFIED", "REJECTED", "WAIVED"].includes(
    normalizedStatus
  );
};

export default function AssistantPage() {
  const {
    assistant,
    advisors,
    leads,
    cases,
    leadPagination,
    casePagination,
    loading,
    error,
    nextLeadPage,
    prevLeadPage,
    nextCasePage,
    prevCasePage,
    refresh,
    createLead,
    bookConsultation,
    sendEngagementLetter,
    convertLeadToClient,
  } = useAssistantDashboard();

  const [submittingLead, setSubmittingLead] = useState(false);
  const [submittingBooking, setSubmittingBooking] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [updatingLeadPhone, setUpdatingLeadPhone] = useState(false);
  const [leadPhoneDraft, setLeadPhoneDraft] = useState("");
  const [leadPhoneError, setLeadPhoneError] = useState<string | null>(null);
  const [leadPhoneSuccess, setLeadPhoneSuccess] = useState<string | null>(null);
  const [initialContactOutcomeDraft, setInitialContactOutcomeDraft] = useState<
    InitialLeadContactOutcome | ""
  >("");
  const [initialContactCallLoggedAt, setInitialContactCallLoggedAt] = useState(
    currentDateTimeLocalInputValue()
  );
  const [savingInitialContactOutcome, setSavingInitialContactOutcome] = useState(false);
  const [initialContactError, setInitialContactError] = useState<string | null>(null);
  const [initialContactSuccess, setInitialContactSuccess] = useState<string | null>(null);
  const [sendingEngagementLetter, setSendingEngagementLetter] = useState(false);
  const [engagementError, setEngagementError] = useState<string | null>(null);
  const [engagementSuccess, setEngagementSuccess] = useState<string | null>(null);
  const [clientSignedDocumentStatus, setClientSignedDocumentStatus] =
    useState<ClientSignedDocumentStatus>("PENDING");
  const [creatingSignedDocumentTask, setCreatingSignedDocumentTask] = useState(false);
  const [signedDocumentTaskError, setSignedDocumentTaskError] = useState<string | null>(null);
  const [signedDocumentTaskSuccess, setSignedDocumentTaskSuccess] = useState<string | null>(null);
  const [convertingLead, setConvertingLead] = useState(false);
  const [conversionError, setConversionError] = useState<string | null>(null);
  const [conversionSuccess, setConversionSuccess] = useState<string | null>(null);
  const [transitioningCaseStage, setTransitioningCaseStage] = useState(false);
  const [caseTransitionError, setCaseTransitionError] = useState<string | null>(null);
  const [caseTransitionSuccess, setCaseTransitionSuccess] = useState<string | null>(null);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  const [activeSelection, setActiveSelection] = useState<ActiveSelection>("lead");
  const [showMoreLeadDetails, setShowMoreLeadDetails] = useState(false);
  const [showMoreCaseDetails, setShowMoreCaseDetails] = useState(false);
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
  const [leadSubmitError, setLeadSubmitError] = useState<string | null>(null);
  const [leadForm, setLeadForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    visa_type: "Visitor visa",
    country_code: "NZ",
    template_id: "",
    source_channel: "web",
    assigned_advisor_id: "",
    enquiry_subject: "",
    notes: "",
  });
  const [bookingDrafts, setBookingDrafts] = useState<Record<string, LeadBookingDraft>>(
    {}
  );
  const [leadStageTasks, setLeadStageTasks] = useState<AssistantLeadTaskItem[]>([]);
  const [caseStageTasks, setCaseStageTasks] = useState<AssistantCaseTaskItem[]>([]);
  const [leadTasksLoading, setLeadTasksLoading] = useState(false);
  const [caseTasksLoading, setCaseTasksLoading] = useState(false);
  const [leadTasksError, setLeadTasksError] = useState<string | null>(null);
  const [caseTasksError, setCaseTasksError] = useState<string | null>(null);
  const [updatingLeadTaskIds, setUpdatingLeadTaskIds] = useState<Record<string, boolean>>({});
  const [updatingCaseTaskIds, setUpdatingCaseTaskIds] = useState<Record<string, boolean>>({});
  const [assignedTasks, setAssignedTasks] = useState<AssistantAssignedTaskItem[]>([]);
  const [assignedTasksLoading, setAssignedTasksLoading] = useState(false);
  const [assignedTasksError, setAssignedTasksError] = useState<string | null>(null);
  const [updatingAssignedTaskIds, setUpdatingAssignedTaskIds] = useState<Record<string, boolean>>(
    {}
  );
  const [workflowTemplatesById, setWorkflowTemplatesById] = useState<
    Record<string, WorkflowTemplateResponse>
  >({});
  const [availableWorkflowTemplates, setAvailableWorkflowTemplates] = useState<
    WorkflowTemplateResponse[]
  >([]);
  const [workflowTemplatesLoading, setWorkflowTemplatesLoading] = useState(false);
  const [workflowTemplatesError, setWorkflowTemplatesError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [runningBulkReminders, setRunningBulkReminders] = useState(false);
  const [bulkReminderError, setBulkReminderError] = useState<string | null>(null);
  const [bulkReminderSuccess, setBulkReminderSuccess] = useState<string | null>(null);
  const [currentStageDocumentsData, setCurrentStageDocumentsData] =
    useState<LeadCurrentStageDocumentsResponse | null>(null);
  const [caseCurrentStageDocumentsData, setCaseCurrentStageDocumentsData] =
    useState<AssistantCaseCurrentStageDocumentsResponse | null>(null);
  const [leadDocumentsLoading, setLeadDocumentsLoading] = useState(false);
  const [caseDocumentsLoading, setCaseDocumentsLoading] = useState(false);
  const [leadDocumentsError, setLeadDocumentsError] = useState<string | null>(null);
  const [caseDocumentsError, setCaseDocumentsError] = useState<string | null>(null);
  const [uploadingDocumentIds, setUploadingDocumentIds] = useState<Record<string, boolean>>({});
  const [reviewingDocumentIds, setReviewingDocumentIds] = useState<Record<string, boolean>>({});
  const [reviewModalDocument, setReviewModalDocument] = useState<LeadStageDocumentItem | null>(null);
  const [reviewModalMode, setReviewModalMode] = useState<"review" | "view">("review");
  const [reviewModalSource, setReviewModalSource] = useState<DocumentModalSource>("lead");
  const [reviewModalError, setReviewModalError] = useState<string | null>(null);
  const [reviewRejectReason, setReviewRejectReason] = useState("");

  useEffect(() => {
    if (leads.length === 0) {
      setSelectedLeadId(null);
      setLeadStageTasks([]);
      setLeadTasksLoading(false);
      setLeadTasksError(null);
      setUpdatingLeadTaskIds({});
      return;
    }

    if (!selectedLeadId || !leads.some((lead) => lead.lead_id === selectedLeadId)) {
      setSelectedLeadId(leads[0].lead_id);
    }
  }, [leads, selectedLeadId]);

  useEffect(() => {
    if (cases.length === 0) {
      setSelectedCaseId(null);
      setCaseStageTasks([]);
      setCaseTasksLoading(false);
      setCaseTasksError(null);
      setUpdatingCaseTaskIds({});
      setCaseCurrentStageDocumentsData(null);
      setCaseDocumentsLoading(false);
      setCaseDocumentsError(null);
      if (activeSelection === "case") {
        setActiveSelection("lead");
      }
      return;
    }

    if (!selectedCaseId || !cases.some((item) => item.case_id === selectedCaseId)) {
      setSelectedCaseId(cases[0].case_id);
    }
  }, [activeSelection, cases, selectedCaseId]);

  useEffect(() => {
    if (activeSelection === "lead" && leads.length === 0 && cases.length > 0) {
      setActiveSelection("case");
    }
  }, [activeSelection, cases.length, leads.length]);

  useEffect(() => {
    if (!selectedLeadId) return;
    if (bookingDrafts[selectedLeadId]) return;

    const lead = leads.find((item) => item.lead_id === selectedLeadId);
    if (!lead) return;

    setBookingDrafts((prev) => ({
      ...prev,
      [selectedLeadId]: buildInitialBookingState(lead),
    }));
  }, [bookingDrafts, leads, selectedLeadId]);

  useEffect(() => {
    setBookingError(null);
    setUpdatingLeadPhone(false);
    setLeadPhoneError(null);
    setLeadPhoneSuccess(null);
    setEngagementError(null);
    setEngagementSuccess(null);
    setConversionError(null);
    setConversionSuccess(null);
    setConvertingLead(false);
    setLeadTasksError(null);
    setUpdatingLeadTaskIds({});
    setShowMoreLeadDetails(false);
    setLeadDocumentsError(null);
    setUploadingDocumentIds({});
    setReviewingDocumentIds({});
    setReviewModalDocument(null);
    setReviewModalMode("review");
    setReviewModalSource("lead");
    setReviewModalError(null);
    setReviewRejectReason("");
  }, [selectedLeadId]);

  useEffect(() => {
    setCaseTasksError(null);
    setUpdatingCaseTaskIds({});
    setShowMoreCaseDetails(false);
    setCaseDocumentsError(null);
    setCaseTransitionError(null);
    setCaseTransitionSuccess(null);
    setTransitioningCaseStage(false);
    setReviewModalDocument(null);
    setReviewModalMode("review");
    setReviewModalSource("lead");
    setReviewModalError(null);
    setReviewRejectReason("");
  }, [selectedCaseId]);

  useEffect(() => {
    if (advisors.length === 0) return;
    const hasSelectedAdvisor = advisors.some(
      (advisor) => advisor.id === leadForm.assigned_advisor_id
    );
    if (hasSelectedAdvisor) return;

    setLeadForm((prev) => ({
      ...prev,
      assigned_advisor_id: advisors[0].id,
    }));
  }, [advisors, leadForm.assigned_advisor_id]);

  useEffect(() => {
    if (!assistant?.firm_id) {
      setAvailableWorkflowTemplates([]);
      setWorkflowTemplatesError(null);
      setWorkflowTemplatesLoading(false);
      return;
    }

    let cancelled = false;

    const loadWorkflowTemplates = async () => {
      setWorkflowTemplatesLoading(true);
      setWorkflowTemplatesError(null);
      try {
        const templates = await assistantService.listWorkflowTemplates({
          firm_id: assistant.firm_id,
          limit: 200,
          offset: 0,
        });
        if (cancelled) return;

        setAvailableWorkflowTemplates(templates);
        setWorkflowTemplatesById((prev) => {
          const next = { ...prev };
          for (const template of templates) {
            next[template.id] = template;
          }
          return next;
        });

        if (templates.length === 0) {
          setLeadForm((prev) => ({
            ...prev,
            template_id: "",
          }));
          return;
        }

        const defaultTemplate = templates.find((template) => template.is_active) ?? templates[0];
        setLeadForm((prev) => {
          const hasSelectedTemplate = templates.some(
            (template) => template.id === prev.template_id
          );
          if (hasSelectedTemplate) return prev;
          return {
            ...prev,
            visa_type: defaultTemplate.visa_type,
            country_code: defaultTemplate.country_code,
            template_id: defaultTemplate.id,
          };
        });
      } catch (err) {
        if (cancelled) return;
        setAvailableWorkflowTemplates([]);
        setWorkflowTemplatesError(
          toErrorMessage(err, "Could not load workflow templates for lead creation.")
        );
      } finally {
        if (!cancelled) {
          setWorkflowTemplatesLoading(false);
        }
      }
    };

    void loadWorkflowTemplates();

    return () => {
      cancelled = true;
    };
  }, [assistant?.firm_id]);

  const selectedLead = useMemo(
    () => leads.find((lead) => lead.lead_id === selectedLeadId) ?? null,
    [leads, selectedLeadId]
  );
  const selectedCase = useMemo(
    () => cases.find((caseItem) => caseItem.case_id === selectedCaseId) ?? null,
    [cases, selectedCaseId]
  );
  const selectedLeadStatus = selectedLead?.status ?? "";
  const selectedLeadStatusUpper = selectedLeadStatus.trim().toUpperCase();
  const selectedLeadFollowupOutcome = (selectedLead?.followup_outcome ?? "").toUpperCase();
  const selectedCaseStatus = (selectedCase?.stage_status ?? "").toUpperCase();
  const selectedLeadTemplate = useMemo(() => {
    if (!selectedLead?.template_id) return null;
    return workflowTemplatesById[selectedLead.template_id] ?? null;
  }, [selectedLead?.template_id, workflowTemplatesById]);
  const selectedCaseTemplate = useMemo(() => {
    if (!selectedCase?.template_id) return null;
    return workflowTemplatesById[selectedCase.template_id] ?? null;
  }, [selectedCase?.template_id, workflowTemplatesById]);
  const defaultLeadWorkflowTemplate = useMemo(
    () =>
      availableWorkflowTemplates.find((template) => template.is_active) ??
      availableWorkflowTemplates[0] ??
      null,
    [availableWorkflowTemplates]
  );
  const selectedLeadWorkflow = useMemo(
    () => (selectedLead ? deriveLeadWorkflow(selectedLeadStatus, selectedLeadTemplate) : null),
    [selectedLead, selectedLeadStatus, selectedLeadTemplate]
  );
  const selectedCaseWorkflow = useMemo(
    () =>
      selectedCase
        ? deriveCaseWorkflow(
            selectedCase.lifecycle_status,
            selectedCase.current_stage_label,
            selectedCaseTemplate
          )
        : null,
    [selectedCase, selectedCaseTemplate]
  );
  const selectedLeadCurrentStageKey = (
    selectedLeadWorkflow?.currentStageKey ?? ""
  ).trim().toLowerCase();
  const isLeadCaptureStage =
    selectedLeadCurrentStageKey === "lead_capture" ||
    selectedLeadCurrentStageKey === "lead_captured";
  const isContactedStage = selectedLeadCurrentStageKey === "contacted";
  const isConsultationStage = selectedLeadCurrentStageKey.includes("consultation");
  const isEligibilityStage = selectedLeadCurrentStageKey.includes("eligibility");
  const isPreConsultationStage = isLeadCaptureStage || isContactedStage;
  const selectedLeadCurrentStageIndex = useMemo(() => {
    if (!selectedLeadWorkflow) return 0;
    if (!selectedLeadWorkflow.currentStageKey) {
      return Math.max(0, selectedLeadWorkflow.currentStageNumber - 1);
    }
    const index = selectedLeadWorkflow.stages.findIndex(
      (stage) => stage.key === selectedLeadWorkflow.currentStageKey
    );
    return index >= 0 ? index : Math.max(0, selectedLeadWorkflow.currentStageNumber - 1);
  }, [selectedLeadWorkflow]);
  const selectedLeadInitialContactOutcome = String(
    selectedLead?.initial_contact_outcome ?? ""
  ).toUpperCase();
  const isLeadInStageOne = Boolean(selectedLead) && selectedLeadCurrentStageIndex === 0;
  const shouldShowInitialContactCapture =
    isLeadCaptureStage && isLeadInStageOne;
  const nextLeadStageLabel = selectedLeadWorkflow?.nextStageLabel ?? null;
  const orderedAssignedTasks = useMemo(() => {
    return [...assignedTasks].sort((a, b) => {
      const aTime = a.due_at ? new Date(a.due_at).getTime() : Number.POSITIVE_INFINITY;
      const bTime = b.due_at ? new Date(b.due_at).getTime() : Number.POSITIVE_INFINITY;
      return aTime - bTime;
    });
  }, [assignedTasks]);
  const leadNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const lead of leads) {
      map.set(lead.lead_id, fullName(lead.first_name, lead.last_name));
    }
    return map;
  }, [leads]);
  const openLeadStageTasks = useMemo(
    () => leadStageTasks.filter((task) => task.status === "OPEN"),
    [leadStageTasks]
  );
  const hasWrongNumberFollowupTask = useMemo(
    () => openLeadStageTasks.some((task) => isWrongNumberTask(task)),
    [openLeadStageTasks]
  );
  const hasNoShowOrFollowupTask = useMemo(
    () => openLeadStageTasks.some((task) => isNoShowRebookTask(task)),
    [openLeadStageTasks]
  );
  const openNonRebookLeadStageTasks = useMemo(
    () =>
      openLeadStageTasks.filter(
        (task) => !isNoShowRebookTask(task) && !isWrongNumberTask(task)
      ),
    [openLeadStageTasks]
  );
  const hasBlockingOpenLeadStageTasksForBooking =
    openNonRebookLeadStageTasks.length > 0;
  const shouldShowRebookingAction =
    isConsultationStage &&
    (
      hasNoShowOrFollowupTask ||
      selectedLeadFollowupOutcome === "NOT_REACHED" ||
      selectedLeadFollowupOutcome === "FOLLOWUP"
    );
  const shouldShowWrongNumberAction =
    hasWrongNumberFollowupTask || selectedLeadFollowupOutcome === "WRONG_NUMBER";
  const isUpdatingLeadTasks = useMemo(
    () => Object.values(updatingLeadTaskIds).some(Boolean),
    [updatingLeadTaskIds]
  );

  const ensureWorkflowTemplateLoaded = useCallback(
    async (templateId: string | null | undefined): Promise<WorkflowTemplateResponse | null> => {
      if (!templateId) return null;
      const existing = workflowTemplatesById[templateId];
      if (existing) return existing;

      const template = await assistantService.getWorkflowTemplate(templateId);
      setWorkflowTemplatesById((prev) =>
        prev[templateId] ? prev : { ...prev, [templateId]: template }
      );
      return template;
    },
    [workflowTemplatesById]
  );

  const fetchLeadStageTasks = useCallback(
    async (lead: LeadListItem | null) => {
      if (!lead) return [];
      const currentUserId = assistant?.id?.toLowerCase();
      if (!currentUserId) return [];

      const workflowTemplate = await ensureWorkflowTemplateLoaded(lead.template_id);
      const workflow = deriveLeadWorkflow(
        lead.status ?? "",
        workflowTemplate
      );
      const stageKey = workflow.currentStageKey;
      if (!stageKey) return [];
      const currentStage = workflow.stages.find((stage) => stage.key === stageKey);
      if (!currentStage || !isAssistantPersonaStage(currentStage)) return [];

      const taskData = await assistantService.getLeadTasks(lead.lead_id);
      return taskData.tasks.filter(
        (task) =>
          task.stage_key === stageKey &&
          typeof task.assigned_to === "string" &&
          task.assigned_to.toLowerCase() === currentUserId
      );
    },
    [assistant?.id, ensureWorkflowTemplateLoaded]
  );

  const fetchCaseStageTasks = useCallback(
    async (caseItem: AssistantCaseListItem | null) => {
      if (!caseItem) return [];
      const currentUserId = assistant?.id?.toLowerCase();
      if (!currentUserId) return [];
      const stageKey = caseItem.lifecycle_status;
      if (!stageKey) return [];

      const taskData = await assistantService.getCaseTasks(caseItem.case_id);
      return taskData.tasks.filter(
        (task) =>
          task.stage_key === stageKey &&
          typeof task.assigned_to === "string" &&
          task.assigned_to.toLowerCase() === currentUserId
      );
    },
    [assistant?.id]
  );

  const loadLeadCurrentStageDocuments = useCallback(
    async (lead: LeadListItem | null) => {
      if (!lead || !lead.template_id) {
        setCurrentStageDocumentsData(null);
        setLeadDocumentsLoading(false);
        setLeadDocumentsError(null);
        return;
      }

      setLeadDocumentsLoading(true);
      setLeadDocumentsError(null);
      try {
        const data = await assistantService.getLeadCurrentStageDocuments(lead.lead_id);
        setCurrentStageDocumentsData(data);
      } catch (err) {
        const errorMessage = toErrorMessage(err, "Could not load current-stage documents.");
        setCurrentStageDocumentsData(null);
        if (
          errorMessage
            .toLowerCase()
            .includes("could not resolve current stage for lead status")
        ) {
          setLeadDocumentsError(null);
          return;
        }
        setLeadDocumentsError(errorMessage);
      } finally {
        setLeadDocumentsLoading(false);
      }
    },
    []
  );

  const loadCaseCurrentStageDocuments = useCallback(
    async (caseItem: AssistantCaseListItem | null) => {
      if (!caseItem) {
        setCaseCurrentStageDocumentsData(null);
        setCaseDocumentsLoading(false);
        setCaseDocumentsError(null);
        return;
      }

      setCaseDocumentsLoading(true);
      setCaseDocumentsError(null);
      try {
        const data = await assistantService.getCaseCurrentStageDocuments(caseItem.case_id);
        setCaseCurrentStageDocumentsData(data);
      } catch (err) {
        setCaseCurrentStageDocumentsData(null);
        setCaseDocumentsError(toErrorMessage(err, "Could not load current-stage documents."));
      } finally {
        setCaseDocumentsLoading(false);
      }
    },
    []
  );

  const loadAssignedTasks = useCallback(async () => {
    if (!assistant?.id) {
      setAssignedTasks([]);
      setAssignedTasksError(null);
      setAssignedTasksLoading(false);
      setUpdatingAssignedTaskIds({});
      return;
    }

    setAssignedTasksLoading(true);
    setAssignedTasksError(null);

    try {
      const taskData = await assistantService.listAssignedTasks({
        status: "OPEN",
        limit: 200,
        offset: 0,
      });
      setAssignedTasks(taskData.tasks);
    } catch (err) {
      setAssignedTasks([]);
      setAssignedTasksError(toErrorMessage(err, "Could not load assigned tasks."));
    } finally {
      setAssignedTasksLoading(false);
    }
  }, [assistant?.id]);

  const handleToggleAssignedTask = useCallback(
    async (task: AssistantAssignedTaskItem) => {
      if (!assistant?.id) return;

      const nextStatus = task.status === "DONE" ? "OPEN" : "DONE";
      setAssignedTasksError(null);
      setUpdatingAssignedTaskIds((prev) => ({
        ...prev,
        [task.task_id]: true,
      }));

      try {
        await assistantService.updateAssignedTaskStatus(task, nextStatus, assistant.id);
        if (
          task.source === "LEAD" &&
          selectedLead &&
          task.source_id.toLowerCase() === selectedLead.lead_id.toLowerCase()
        ) {
          const refreshedStageTasks = await fetchLeadStageTasks(selectedLead);
          setLeadStageTasks(refreshedStageTasks);
        }
        if (
          task.source === "CASE" &&
          selectedCase &&
          task.source_id.toLowerCase() === selectedCase.case_id.toLowerCase()
        ) {
          const refreshedCaseTasks = await fetchCaseStageTasks(selectedCase);
          setCaseStageTasks(refreshedCaseTasks);
        }
        await loadAssignedTasks();
      } catch (err) {
        setAssignedTasksError(toErrorMessage(err, "Could not update task status."));
      } finally {
        setUpdatingAssignedTaskIds((prev) => ({
          ...prev,
          [task.task_id]: false,
        }));
      }
    },
    [assistant?.id, fetchCaseStageTasks, fetchLeadStageTasks, loadAssignedTasks, selectedCase, selectedLead]
  );

  const selectedLeadBooking = selectedLeadId ? bookingDrafts[selectedLeadId] : undefined;

  useEffect(() => {
    if (!selectedLead) {
      setLeadPhoneDraft("");
      setInitialContactOutcomeDraft("");
      setInitialContactCallLoggedAt(currentDateTimeLocalInputValue());
      setInitialContactError(null);
      setInitialContactSuccess(null);
      return;
    }
    setLeadPhoneDraft(selectedLead.phone ?? "");
    setInitialContactCallLoggedAt(currentDateTimeLocalInputValue());
    const normalizedOutcome = String(selectedLead.initial_contact_outcome ?? "").toUpperCase();
    if (
      normalizedOutcome === "CONTACTED" ||
      normalizedOutcome === "NO_ANSWER" ||
      normalizedOutcome === "WRONG_NUMBER"
    ) {
      setInitialContactOutcomeDraft(normalizedOutcome as InitialLeadContactOutcome);
    } else {
      setInitialContactOutcomeDraft("");
    }
    setInitialContactError(null);
    setInitialContactSuccess(null);
  }, [selectedLead]);

  const selectedLeadAdvisorName = useMemo(() => {
    if (!selectedLead?.assigned_advisor_id) return "Unassigned";
    const advisor = advisors.find((item) => item.id === selectedLead.assigned_advisor_id);
    return advisor ? fullName(advisor.first_name, advisor.last_name) : "Unassigned";
  }, [advisors, selectedLead?.assigned_advisor_id]);
  const selectedCaseAdvisorName = useMemo(() => {
    if (!selectedCase?.assigned_advisor_id) return "Unassigned";
    const advisor = advisors.find((item) => item.id === selectedCase.assigned_advisor_id);
    return advisor
      ? fullName(advisor.first_name, advisor.last_name)
      : selectedCase.assigned_advisor_id;
  }, [advisors, selectedCase?.assigned_advisor_id]);
  const selectedCaseAssistantLabel = useMemo(() => {
    if (!selectedCase?.assigned_assistant_id) return "Unassigned";
    if (assistant?.id && selectedCase.assigned_assistant_id === assistant.id) return "You";
    return selectedCase.assigned_assistant_id;
  }, [assistant?.id, selectedCase?.assigned_assistant_id]);

  useEffect(() => {
    if (!selectedLead?.template_id) return;
    void ensureWorkflowTemplateLoaded(selectedLead.template_id).catch(() => {
      // Template load errors are surfaced through stage-task fetch paths.
    });
  }, [ensureWorkflowTemplateLoaded, selectedLead?.template_id]);

  useEffect(() => {
    if (!selectedCase?.template_id) return;
    void ensureWorkflowTemplateLoaded(selectedCase.template_id).catch(() => {
      // Template load errors are surfaced through stage-task fetch paths.
    });
  }, [ensureWorkflowTemplateLoaded, selectedCase?.template_id]);

  useEffect(() => {
    let cancelled = false;
    if (!selectedLead) {
      setLeadStageTasks([]);
      setLeadTasksLoading(false);
      setLeadTasksError(null);
      return;
    }

    const loadLeadTasks = async () => {
      setLeadTasksLoading(true);
      setLeadTasksError(null);
      try {
        const stageTasks = await fetchLeadStageTasks(selectedLead);
        if (cancelled) return;
        setLeadStageTasks(stageTasks);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof Error && err.message.trim()) {
          setLeadTasksError(err.message);
        } else {
          setLeadTasksError("Could not load stage tasks.");
        }
        setLeadStageTasks([]);
      } finally {
        if (!cancelled) {
          setLeadTasksLoading(false);
        }
      }
    };

    void loadLeadTasks();

    return () => {
      cancelled = true;
    };
  }, [fetchLeadStageTasks, selectedLead]);

  useEffect(() => {
    if (!selectedLead) {
      setCurrentStageDocumentsData(null);
      setLeadDocumentsLoading(false);
      setLeadDocumentsError(null);
      return;
    }
    void loadLeadCurrentStageDocuments(selectedLead);
  }, [loadLeadCurrentStageDocuments, selectedLead]);

  useEffect(() => {
    let cancelled = false;
    if (!selectedCase) {
      setCaseStageTasks([]);
      setCaseTasksLoading(false);
      setCaseTasksError(null);
      return;
    }

    const loadCaseTasks = async () => {
      setCaseTasksLoading(true);
      setCaseTasksError(null);
      try {
        const stageTasks = await fetchCaseStageTasks(selectedCase);
        if (cancelled) return;
        setCaseStageTasks(stageTasks);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof Error && err.message.trim()) {
          setCaseTasksError(err.message);
        } else {
          setCaseTasksError("Could not load case stage tasks.");
        }
        setCaseStageTasks([]);
      } finally {
        if (!cancelled) {
          setCaseTasksLoading(false);
        }
      }
    };

    void loadCaseTasks();

    return () => {
      cancelled = true;
    };
  }, [fetchCaseStageTasks, selectedCase]);

  useEffect(() => {
    if (!selectedCase) {
      setCaseCurrentStageDocumentsData(null);
      setCaseDocumentsLoading(false);
      setCaseDocumentsError(null);
      return;
    }
    void loadCaseCurrentStageDocuments(selectedCase);
  }, [loadCaseCurrentStageDocuments, selectedCase]);

  useEffect(() => {
    void loadAssignedTasks();
  }, [loadAssignedTasks]);

  const stageSteps = useMemo<WorkflowStep[]>(() => {
    if (!selectedLeadWorkflow) return [];
    return selectedLeadWorkflow.stages.map((stage) => {
      const stageIndex = selectedLeadWorkflow.stages.findIndex(
        (candidate) => candidate.key === stage.key
      );
      const normalizedStageIndex = stageIndex >= 0 ? stageIndex : 0;
      return {
        id: stage.key,
        label: stage.label,
        state:
          normalizedStageIndex < selectedLeadCurrentStageIndex
            ? "done"
            : normalizedStageIndex === selectedLeadCurrentStageIndex
            ? "active"
            : "locked",
      };
    });
  }, [selectedLeadCurrentStageIndex, selectedLeadWorkflow]);

  const currentStageDocuments = useMemo(
    () => currentStageDocumentsData?.documents ?? [],
    [currentStageDocumentsData?.documents]
  );
  const caseCurrentStageDocuments = useMemo(
    () => caseCurrentStageDocumentsData?.documents ?? [],
    [caseCurrentStageDocumentsData?.documents]
  );
  const normalizedStageContext = `${selectedLeadWorkflow?.currentStageKey ?? ""} ${
    selectedLeadWorkflow?.currentStageLabel ?? ""
  }`
    .trim()
    .toLowerCase();
  const isEngagementStage =
    normalizedStageContext.includes("engagement") || normalizedStageContext.includes("enagement");
  const engagementLetterDocument = useMemo(
    () =>
      currentStageDocuments.find((document) => {
        const normalizedLabel = String(document.label ?? "").trim().toLowerCase();
        const normalizedCode = String(document.doc_code ?? "").trim().toLowerCase();
        return normalizedLabel.includes("engagement letter") || normalizedCode === "d4_1";
      }) ?? null,
    [currentStageDocuments]
  );
  const hasUploadedEngagementLetter = Boolean(
    engagementLetterDocument && hasDocumentUploaded(engagementLetterDocument)
  );
  const engagementLetterReviewAction = String(
    engagementLetterDocument?.metadata_json?.last_review_action ?? ""
  ).toUpperCase();
  const engagementLetterReviewedByRaw =
    engagementLetterDocument?.metadata_json?.last_reviewed_by;
  const engagementLetterReviewedBy =
    typeof engagementLetterReviewedByRaw === "string" ? engagementLetterReviewedByRaw : "";
  const normalizedAssignedAdvisorId = String(selectedLead?.assigned_advisor_id ?? "").toLowerCase();
  const isEngagementLetterAdvisorApproved = Boolean(engagementLetterDocument) &&
    String(engagementLetterDocument?.status ?? "").toUpperCase() === "VERIFIED" &&
    engagementLetterReviewAction === "VERIFIED" &&
    Boolean(normalizedAssignedAdvisorId) &&
    engagementLetterReviewedBy.toLowerCase() === normalizedAssignedAdvisorId;
  const isEngagementLetterSent =
    ["ENGAGEMENT_LETTER_SENT", "AGREEMENT_SENT"].includes(
      String(selectedLead?.followup_email_status ?? "").toUpperCase()
    ) || Boolean(engagementSuccess);
  const engagementSignedDocumentTask = useMemo(
    () =>
      leadStageTasks.find((task) => {
        const normalizedCode = leadTaskCode(task);
        if (normalizedCode === ENGAGEMENT_SIGNED_DOCUMENT_TASK_CODE) return true;
        return task.title.toLowerCase().includes("signed engagement document");
      }) ?? null,
    [leadStageTasks]
  );
  const isSignedDocumentTaskDone = engagementSignedDocumentTask?.status === "DONE";
  const selectedCaseOpenTasks = useMemo(
    () => caseStageTasks.filter((task) => task.status === "OPEN"),
    [caseStageTasks]
  );
  const caseStageSteps = useMemo<WorkflowStep[]>(
    () => {
      if (!selectedCaseWorkflow) return [];
      const isClosed = Boolean(selectedCase?.closed_at);
      return selectedCaseWorkflow.stages.map((stage, index) => ({
        id: stage.key,
        label: stage.label,
        state: isClosed
          ? "done"
          : index < selectedCaseWorkflow.currentStageIndex
          ? "done"
          : index === selectedCaseWorkflow.currentStageIndex
          ? "active"
          : "locked",
      }));
    },
    [selectedCase?.closed_at, selectedCaseWorkflow]
  );
  const nextCaseStageKey = selectedCaseWorkflow?.nextStageKey ?? null;
  const nextCaseStageLabel = selectedCaseWorkflow?.nextStageLabel ?? null;
  const hasNextCaseStage = Boolean(nextCaseStageKey);
  const caseAction = selectedCase
    ? hasNextCaseStage
      ? {
          title: `Move to ${
            nextCaseStageLabel ?? toLabel(nextCaseStageKey ?? "next_stage")
          }`,
          detail:
            "Complete current-stage tasks and required documents, then progress this case to the next workflow stage.",
        }
      : {
          title: "Review case completion",
          detail: "This case has reached the final configured workflow stage.",
        }
    : DEFAULT_CASE_ACTION;
  const normalizedNextCaseStageKey = normalizeCaseStageToken(nextCaseStageKey);
  const isNextCaseStageClosure =
    normalizedNextCaseStageKey.includes("clos") || normalizedNextCaseStageKey === "closed";
  const isNextCaseStageFinal = Boolean(
    selectedCaseWorkflow &&
      selectedCaseWorkflow.currentStageIndex + 1 === selectedCaseWorkflow.stages.length - 1
  );
  const caseBlockingDocuments = caseCurrentStageDocuments.filter(
    (document) => document.is_required && document.blocks_stage_exit
  );
  const allCaseBlockingDocumentsReviewed = caseBlockingDocuments.every(
    (document) => document.status === "VERIFIED" || document.status === "WAIVED"
  );
  const hasOpenCaseStageTasks = selectedCaseOpenTasks.length > 0;
  const isUpdatingCaseTasks = useMemo(
    () => Object.values(updatingCaseTaskIds).some(Boolean),
    [updatingCaseTaskIds]
  );
  const canAdvanceCaseStage = Boolean(selectedCase) &&
    hasNextCaseStage &&
    !caseTasksLoading &&
    !caseDocumentsLoading &&
    !isUpdatingCaseTasks &&
    !hasOpenCaseStageTasks &&
    allCaseBlockingDocumentsReviewed;
  const advanceCaseButtonLabel = isNextCaseStageClosure || isNextCaseStageFinal
    ? "Mark case closed"
    : `Move to ${nextCaseStageLabel ?? toLabel(nextCaseStageKey ?? "next_stage")}`;
  const canConvertLeadToClient = Boolean(selectedLead) &&
    isEngagementStage &&
    isEngagementLetterSent &&
    isSignedDocumentTaskDone;

  useEffect(() => {
    setSignedDocumentTaskError(null);
    setSignedDocumentTaskSuccess(null);
    if (!selectedLeadId) {
      setClientSignedDocumentStatus("PENDING");
      return;
    }
    if (engagementSignedDocumentTask) {
      setClientSignedDocumentStatus("RECEIVED");
      return;
    }
    setClientSignedDocumentStatus("PENDING");
  }, [selectedLeadId, engagementSignedDocumentTask]);

  const noWorkflowStagesConfigured = Boolean(selectedLead) && stageSteps.length === 0;
  const createLeadEmailInvalid =
    leadForm.email.trim().length > 0 && !isValidEmail(leadForm.email);
  const isLeadFormComplete = useMemo(() => {
    const hasCountryCode = leadForm.country_code.trim().length === 2;
    const requiresWorkflowTemplate = availableWorkflowTemplates.length > 0;
    const hasWorkflowTemplate = !requiresWorkflowTemplate || leadForm.template_id.trim().length > 0;
    const hasValidEmail = isValidEmail(leadForm.email);
    return (
      leadForm.first_name.trim().length > 0 &&
      leadForm.last_name.trim().length > 0 &&
      hasValidEmail &&
      leadForm.visa_type.trim().length > 0 &&
      hasWorkflowTemplate &&
      hasCountryCode
    );
  }, [availableWorkflowTemplates.length, leadForm]);
  const isCreateLeadDisabled = submittingLead || !isLeadFormComplete;

  const handleLeadFormChange = (field: keyof typeof leadForm, value: string) => {
    setLeadForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleLeadCountryCodeChange = (countryCode: string) => {
    setLeadForm((prev) => {
      const next = { ...prev, country_code: countryCode };
      if (!prev.template_id) return next;
      const selectedTemplate = availableWorkflowTemplates.find(
        (template) => template.id === prev.template_id
      );
      if (
        selectedTemplate &&
        selectedTemplate.country_code.toUpperCase() !== countryCode.trim().toUpperCase()
      ) {
        next.template_id = "";
      }
      return next;
    });
  };

  const handleLeadTemplateChange = (templateId: string) => {
    if (!templateId) {
      setLeadForm((prev) => ({
        ...prev,
        template_id: "",
      }));
      return;
    }

    const selectedTemplate = availableWorkflowTemplates.find(
      (template) => template.id === templateId
    );
    if (!selectedTemplate) {
      setLeadForm((prev) => ({ ...prev, template_id: templateId }));
      return;
    }

    setLeadForm((prev) => ({
      ...prev,
      template_id: selectedTemplate.id,
      visa_type: selectedTemplate.visa_type,
      country_code: selectedTemplate.country_code,
    }));
  };

  const handleCreateLead = async () => {
    if (!isLeadFormComplete) {
      if (!isValidEmail(leadForm.email)) {
        setLeadSubmitError("Please enter a valid email.");
        return;
      }
      setLeadSubmitError(
        availableWorkflowTemplates.length > 0
          ? "First name, last name, email, visa type, workflow template, and a 2-letter country code are required."
          : "First name, last name, email, visa type, and a 2-letter country code are required."
      );
      return;
    }

    setSubmittingLead(true);
    setLeadSubmitError(null);
    try {
      await createLead({
        first_name: leadForm.first_name.trim(),
        last_name: leadForm.last_name.trim(),
        email: leadForm.email.trim(),
        phone: leadForm.phone.trim(),
        visa_type: leadForm.visa_type.trim(),
        country_code: leadForm.country_code.trim().toUpperCase(),
        template_id: leadForm.template_id.trim() || undefined,
        source_channel: leadForm.source_channel.trim(),
        assigned_advisor_id: leadForm.assigned_advisor_id,
        enquiry_subject: leadForm.enquiry_subject.trim(),
        notes: leadForm.notes.trim() || undefined,
      });

      setLeadForm({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        visa_type: defaultLeadWorkflowTemplate?.visa_type ?? "Visitor visa",
        country_code: defaultLeadWorkflowTemplate?.country_code ?? "NZ",
        template_id: defaultLeadWorkflowTemplate?.id ?? "",
        source_channel: "web",
        assigned_advisor_id: advisors[0]?.id ?? "",
        enquiry_subject: "",
        notes: "",
      });
      setIsLeadModalOpen(false);
      window.location.reload();
    } catch (err) {
      if (err instanceof Error && err.message.trim()) {
        setLeadSubmitError(err.message);
      } else {
        setLeadSubmitError("Failed to create lead.");
      }
    } finally {
      setSubmittingLead(false);
    }
  };

  const handleBookingFieldChange = (
    leadId: string,
    field: keyof LeadBookingDraft,
    value: string
  ) => {
    if (bookingError) {
      setBookingError(null);
    }

    setBookingDrafts((prev) => ({
      ...prev,
      [leadId]: {
        consultationDate: prev[leadId]?.consultationDate ?? "",
        consultationNotes: prev[leadId]?.consultationNotes ?? "",
        qualificationOutcome: prev[leadId]?.qualificationOutcome ?? "",
        [field]: value,
      },
    }));
  };

  const handleLeadPhoneDraftChange = (value: string) => {
    setLeadPhoneDraft(value);
    if (leadPhoneError) {
      setLeadPhoneError(null);
    }
    if (leadPhoneSuccess) {
      setLeadPhoneSuccess(null);
    }
  };

  const handleInitialContactOutcomeDraftChange = (value: string) => {
    const normalized = value.trim().toUpperCase();
    if (
      normalized === "CONTACTED" ||
      normalized === "NO_ANSWER" ||
      normalized === "WRONG_NUMBER"
    ) {
      setInitialContactOutcomeDraft(normalized as InitialLeadContactOutcome);
    } else {
      setInitialContactOutcomeDraft("");
    }
    if (initialContactError) {
      setInitialContactError(null);
    }
    if (initialContactSuccess) {
      setInitialContactSuccess(null);
    }
  };

  const handleInitialContactCallLoggedAtChange = (value: string) => {
    setInitialContactCallLoggedAt(value);
    if (initialContactError) {
      setInitialContactError(null);
    }
    if (initialContactSuccess) {
      setInitialContactSuccess(null);
    }
  };

  const handleSaveInitialContactOutcome = async () => {
    if (!selectedLead) {
      setInitialContactError("Select a lead first.");
      return;
    }
    if (!shouldShowInitialContactCapture) {
      setInitialContactError("Initial contact update is available only in Stage 1.");
      return;
    }
    const normalizedPhone = leadPhoneDraft.trim();
    const requiresCallLoggedAt =
      initialContactOutcomeDraft === "CONTACTED" ||
      initialContactOutcomeDraft === "NO_ANSWER";
    const normalizedCallLoggedAt = dateTimeLocalToIso(initialContactCallLoggedAt);
    const hasOutcome = Boolean(initialContactOutcomeDraft);
    const hasRequiredPhone =
      initialContactOutcomeDraft !== "WRONG_NUMBER" || normalizedPhone.length > 0;
    const hasRequiredCallLoggedAt =
      !requiresCallLoggedAt || Boolean(normalizedCallLoggedAt);
    const canRecordOutcome =
      hasOutcome &&
      hasRequiredPhone &&
      hasRequiredCallLoggedAt;
    const existingPhone = (selectedLead.phone ?? "").trim();
    const canUpdatePhoneOnly =
      normalizedPhone.length > 0 &&
      normalizedPhone !== existingPhone &&
      !canRecordOutcome;

    if (!canRecordOutcome && !canUpdatePhoneOnly) {
      if (!hasOutcome) {
        setInitialContactError("Select contact outcome or change phone number to save partially.");
        return;
      }
      if (!hasRequiredPhone) {
        setInitialContactError("Enter the correct phone number to save wrong-number outcome.");
        return;
      }
      if (!hasRequiredCallLoggedAt) {
        setInitialContactError("Select call log date and time to save this outcome.");
        return;
      }
    }

    setSavingInitialContactOutcome(true);
    setInitialContactError(null);
    setInitialContactSuccess(null);
    setLeadPhoneError(null);
    setLeadPhoneSuccess(null);
    setBookingError(null);

    try {
      let phoneUpdateResult: { phone: string | null } | null = null;
      if (canUpdatePhoneOnly) {
        phoneUpdateResult = await assistantService.updateLeadPhone(selectedLead.lead_id, {
          phone: normalizedPhone,
        });
      }

      let outcomeResult: {
        status: string;
        phone: string | null;
        followup_task_created: boolean;
      } | null = null;
      if (canRecordOutcome && initialContactOutcomeDraft) {
        outcomeResult = await assistantService.recordInitialLeadContactOutcome(selectedLead.lead_id, {
          outcome: initialContactOutcomeDraft,
          phone: initialContactOutcomeDraft === "WRONG_NUMBER" ? normalizedPhone : undefined,
          call_logged_at: requiresCallLoggedAt ? normalizedCallLoggedAt ?? undefined : undefined,
          contacted_by: assistant?.id,
        });
      }

      await Promise.all([refresh(), loadAssignedTasks()]);
      setLeadPhoneDraft(
        outcomeResult?.phone ?? phoneUpdateResult?.phone ?? normalizedPhone
      );

      if (canRecordOutcome && initialContactOutcomeDraft === "CONTACTED") {
        const movedToNextStage =
          selectedLead.status.toLowerCase() !== (outcomeResult?.status ?? selectedLead.status).toLowerCase();
        setInitialContactSuccess(
          movedToNextStage
            ? `Lead marked as contacted. Lead moved to ${nextLeadStageLabel ?? "the next stage"}.`
            : "Lead marked as contacted. Stage remains unchanged until all stage requirements are complete."
        );
      } else if (canRecordOutcome && initialContactOutcomeDraft === "NO_ANSWER") {
        setInitialContactSuccess(
          outcomeResult?.followup_task_created
            ? "No-answer recorded. Re-contact task created for 1 day later."
            : "No-answer recorded. Existing re-contact task is already open."
        );
      } else if (canRecordOutcome && initialContactOutcomeDraft === "WRONG_NUMBER") {
        setInitialContactSuccess("Wrong number recorded. Lead contact number updated.");
      } else {
        setInitialContactSuccess(
          "Partial lead capture saved. Complete remaining fields to save contact outcome."
        );
      }
    } catch (err) {
      setInitialContactError(toErrorMessage(err, "Could not save initial contact outcome."));
    } finally {
      setSavingInitialContactOutcome(false);
    }
  };

  const handleUpdateLeadPhone = async () => {
    if (!selectedLeadId) {
      setLeadPhoneError("Select a lead first.");
      return;
    }

    const phone = leadPhoneDraft.trim();
    if (!phone) {
      setLeadPhoneError("Phone number is required.");
      return;
    }

    setUpdatingLeadPhone(true);
    setLeadPhoneError(null);
    setLeadPhoneSuccess(null);
    setBookingError(null);

    try {
      const result = await assistantService.updateLeadPhone(selectedLeadId, { phone });
      await Promise.all([refresh(), loadAssignedTasks()]);
      setLeadPhoneDraft(result.phone ?? phone);
      setLeadPhoneSuccess("Lead phone number updated.");
    } catch (err) {
      setLeadPhoneError(toErrorMessage(err, "Could not update lead phone number."));
    } finally {
      setUpdatingLeadPhone(false);
    }
  };

  const handleToggleLeadTask = async (task: AssistantLeadTaskItem) => {
    if (!selectedLead) return;
    const nextStatus = task.status === "DONE" ? "OPEN" : "DONE";

    setUpdatingLeadTaskIds((prev) => ({
      ...prev,
      [task.task_id]: true,
    }));
    setLeadTasksError(null);

    try {
      await assistantService.updateLeadTaskStatus(selectedLead.lead_id, task.task_id, nextStatus);
      const refreshedTasks = await fetchLeadStageTasks(selectedLead);
      setLeadStageTasks(refreshedTasks);
      await loadAssignedTasks();
    } catch (err) {
      if (err instanceof Error && err.message.trim()) {
        setLeadTasksError(err.message);
      } else {
        setLeadTasksError("Could not update stage task.");
      }
    } finally {
      setUpdatingLeadTaskIds((prev) => ({
        ...prev,
        [task.task_id]: false,
      }));
    }
  };

  const handleToggleCaseTask = async (task: AssistantCaseTaskItem) => {
    if (!selectedCase) return;
    const nextStatus = task.status === "DONE" ? "OPEN" : "DONE";

    setUpdatingCaseTaskIds((prev) => ({
      ...prev,
      [task.task_id]: true,
    }));
    setCaseTasksError(null);

    try {
      await assistantService.updateCaseTaskStatus(
        selectedCase.case_id,
        task.task_id,
        nextStatus,
        assistant?.id
      );
      const refreshedTasks = await fetchCaseStageTasks(selectedCase);
      setCaseStageTasks(refreshedTasks);
      await loadAssignedTasks();
    } catch (err) {
      if (err instanceof Error && err.message.trim()) {
        setCaseTasksError(err.message);
      } else {
        setCaseTasksError("Could not update case stage task.");
      }
    } finally {
      setUpdatingCaseTaskIds((prev) => ({
        ...prev,
        [task.task_id]: false,
      }));
    }
  };

  const handleSelectLead = useCallback((leadId: string) => {
    setActiveSelection("lead");
    setSelectedLeadId(leadId);
  }, []);

  const handleSelectCase = useCallback((caseId: string) => {
    setActiveSelection("case");
    setSelectedCaseId(caseId);
  }, []);

  const handleBookConsultation = async () => {
    if (!selectedLeadId) {
      setBookingError("Select a lead first.");
      return;
    }
    if (shouldShowInitialContactCapture && selectedLeadInitialContactOutcome !== "CONTACTED") {
      setBookingError("Mark lead as Contacted in Stage 1 before booking consultation.");
      return;
    }

    if (!selectedLeadBooking?.consultationDate) {
      setBookingError("Consultation date is required.");
      return;
    }
    if (hasBlockingOpenLeadStageTasksForBooking) {
      setBookingError("Complete all your current-stage tasks before booking consultation.");
      return;
    }

    setSubmittingBooking(true);
    setBookingError(null);
    setEngagementError(null);
    setEngagementSuccess(null);
    try {
      await bookConsultation(selectedLeadId, {
        consultation_date: selectedLeadBooking.consultationDate,
        consultation_notes: selectedLeadBooking.consultationNotes || undefined,
        qualification_outcome: selectedLeadBooking.qualificationOutcome || undefined,
      });
      await loadAssignedTasks();
    } catch (err) {
      if (err instanceof Error && err.message.trim()) {
        setBookingError(err.message);
      } else {
        setBookingError("Could not book consultation.");
      }
    } finally {
      setSubmittingBooking(false);
    }
  };

  const handleSendEngagementLetter = async () => {
    if (!selectedLeadId) {
      setEngagementError("Select a lead first.");
      return;
    }

    setSendingEngagementLetter(true);
    setEngagementError(null);
    setEngagementSuccess(null);
    setBookingError(null);
    setConversionError(null);
    setConversionSuccess(null);
    setSignedDocumentTaskError(null);
    setSignedDocumentTaskSuccess(null);
    try {
      await sendEngagementLetter(selectedLeadId);
      await loadAssignedTasks();
      setEngagementSuccess("Engagement letter sent.");
      setClientSignedDocumentStatus("PENDING");
    } catch (err) {
      if (err instanceof Error && err.message.trim()) {
        setEngagementError(err.message);
      } else {
        setEngagementError("Could not send engagement letter.");
      }
    } finally {
      setSendingEngagementLetter(false);
    }
  };

  const handleCreateSignedDocumentUploadTask = async (
    selectedStatus?: ClientSignedDocumentStatus
  ) => {
    if (!selectedLeadId || !selectedLead) {
      setSignedDocumentTaskError("Select a lead first.");
      return;
    }
    if (creatingSignedDocumentTask || engagementSignedDocumentTask) {
      return;
    }
    const effectiveStatus = selectedStatus ?? clientSignedDocumentStatus;
    if (effectiveStatus !== "RECEIVED") {
      setSignedDocumentTaskError(
        "Mark client signed document as received before creating upload task."
      );
      return;
    }

    setCreatingSignedDocumentTask(true);
    setSignedDocumentTaskError(null);
    setSignedDocumentTaskSuccess(null);
    setEngagementError(null);
    try {
      await assistantService.createEngagementSignedDocumentTask(selectedLeadId);
      const refreshedStageTasks = await fetchLeadStageTasks(selectedLead);
      setLeadStageTasks(refreshedStageTasks);
      await loadAssignedTasks();
      setSignedDocumentTaskSuccess(
        "Signed-document upload task created for assistant. Complete it to enable client conversion."
      );
    } catch (err) {
      const maybeHttpError =
        typeof err === "object" && err !== null
          ? (err as { response?: { status?: number; data?: { detail?: string } } })
          : null;
      if (maybeHttpError?.response?.status === 404) {
        setSignedDocumentTaskError(
          "Signed-document task endpoint is not available on the API. Restart/redeploy the CaseFlow backend to pick up the latest routes."
        );
      } else if (err instanceof Error && err.message.trim()) {
        setSignedDocumentTaskError(err.message);
      } else {
        setSignedDocumentTaskError("Could not create signed-document upload task.");
      }
    } finally {
      setCreatingSignedDocumentTask(false);
    }
  };

  const handleConvertLeadToClient = async () => {
    if (!selectedLeadId) {
      setConversionError("Select a lead first.");
      return;
    }

    setConvertingLead(true);
    setConversionError(null);
    setConversionSuccess(null);
    setEngagementError(null);
    setBookingError(null);
    try {
      const result = await convertLeadToClient(selectedLeadId);
      await loadAssignedTasks();
      setConversionSuccess(
        `Lead converted to client case ${result.case_number}. Client user created.`
      );
    } catch (err) {
      if (err instanceof Error && err.message.trim()) {
        setConversionError(err.message);
      } else {
        setConversionError("Could not convert lead to client.");
      }
    } finally {
      setConvertingLead(false);
    }
  };

  const handleAdvanceCaseStage = async () => {
    if (!selectedCase) {
      setCaseTransitionError("Select a case first.");
      return;
    }

    setCaseTransitionError(null);
    setCaseTransitionSuccess(null);

    if (!nextCaseStageKey) {
      setCaseTransitionError("Could not resolve next stage from workflow progression.");
      return;
    }

    if (hasOpenCaseStageTasks || !allCaseBlockingDocumentsReviewed) {
      const pendingDocumentLabels = caseBlockingDocuments
        .filter((document) => document.status !== "VERIFIED" && document.status !== "WAIVED")
        .slice(0, 3)
        .map((document) => document.label)
        .join(", ");
      const pendingDocumentMessage = pendingDocumentLabels
        ? ` Pending required documents: ${pendingDocumentLabels}.`
        : "";
      setCaseTransitionError(
        "Complete all current-stage tasks and verify required blocking documents before moving to the next stage." +
          pendingDocumentMessage
      );
      return;
    }

    setTransitioningCaseStage(true);
    try {
      const result = await assistantService.transitionCaseStage(selectedCase.case_id, {
        lifecycle_status: nextCaseStageKey,
        stage_status: isNextCaseStageClosure || isNextCaseStageFinal ? "DONE" : "ACTIVE",
        current_stage_label: nextCaseStageLabel ?? toLabel(nextCaseStageKey),
      });
      await refresh();
      await loadAssignedTasks();
      setCaseTransitionSuccess(
        `Case moved to ${result.current_stage_label ?? toLabel(result.lifecycle_status)}.`
      );
    } catch (err) {
      setCaseTransitionError(toErrorMessage(err, "Could not move case to next stage."));
    } finally {
      setTransitioningCaseStage(false);
    }
  };

  const updateDocumentInState = useCallback((nextDocument: LeadStageDocumentItem) => {
    setCurrentStageDocumentsData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        documents: prev.documents.map((item) =>
          item.document_item_id === nextDocument.document_item_id ? nextDocument : item
        ),
      };
    });
  }, []);

  const updateCaseDocumentInState = useCallback((nextDocument: LeadStageDocumentItem) => {
    setCaseCurrentStageDocumentsData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        documents: prev.documents.map((item) =>
          item.document_item_id === nextDocument.document_item_id ? nextDocument : item
        ),
      };
    });
  }, []);

  const handleUploadDocument = useCallback(
    async (document: LeadStageDocumentItem, file: File) => {
      if (!selectedLead) return;

      setLeadDocumentsError(null);
      setUploadingDocumentIds((prev) => ({
        ...prev,
        [document.document_item_id]: true,
      }));
      try {
        const result = await assistantService.uploadLeadDocument(
          selectedLead.lead_id,
          document.document_item_id,
          file,
          assistant?.id
        );
        updateDocumentInState(result.document);
      } catch (err) {
        setLeadDocumentsError(toErrorMessage(err, "Could not upload document."));
      } finally {
        setUploadingDocumentIds((prev) => ({
          ...prev,
          [document.document_item_id]: false,
        }));
      }
    },
    [assistant?.id, selectedLead, updateDocumentInState]
  );

  const openDocumentReviewModal = useCallback(
    (document: LeadStageDocumentItem, source: DocumentModalSource) => {
      setReviewModalSource(source);
      setReviewModalDocument(document);
      setReviewModalMode("review");
      setReviewRejectReason(document.rejection_reason ?? "");
      setReviewModalError(null);
    },
    []
  );

  const openDocumentViewModal = useCallback(
    (document: LeadStageDocumentItem, source: DocumentModalSource) => {
      setReviewModalSource(source);
      setReviewModalDocument(document);
      setReviewModalMode("view");
      setReviewRejectReason(document.rejection_reason ?? "");
      setReviewModalError(null);
    },
    []
  );

  const handleReviewAction = useCallback(
    async (action: LeadDocumentReviewAction) => {
      if (!reviewModalDocument) return;

      if (reviewModalSource === "case" && !selectedCase) return;
      if (reviewModalSource === "lead" && !selectedLead) return;

      setReviewModalError(null);
      setReviewingDocumentIds((prev) => ({
        ...prev,
        [reviewModalDocument.document_item_id]: true,
      }));
      try {
        if (reviewModalSource === "case" && selectedCase) {
          const result = await assistantService.reviewCaseDocument(
            selectedCase.case_id,
            reviewModalDocument.document_item_id,
            {
              action,
              reviewed_by: assistant?.id,
              rejection_reason:
                action === "REJECT" ? reviewRejectReason.trim() || undefined : undefined,
            }
          );
          updateCaseDocumentInState(result.document);
        } else if (selectedLead) {
          const result = await assistantService.reviewLeadDocument(
            selectedLead.lead_id,
            reviewModalDocument.document_item_id,
            {
              action,
              reviewed_by: assistant?.id,
              rejection_reason:
                action === "REJECT" ? reviewRejectReason.trim() || undefined : undefined,
            }
          );
          updateDocumentInState(result.document);
        }
        setReviewModalDocument(null);
        setReviewModalMode("review");
        setReviewModalSource("lead");
        setReviewRejectReason("");
      } catch (err) {
        setReviewModalError(toErrorMessage(err, "Could not update document review."));
      } finally {
        setReviewingDocumentIds((prev) => ({
          ...prev,
          [reviewModalDocument.document_item_id]: false,
        }));
      }
    },
    [
      assistant?.id,
      reviewModalDocument,
      reviewModalSource,
      reviewRejectReason,
      selectedCase,
      selectedLead,
      updateCaseDocumentInState,
      updateDocumentInState,
    ]
  );

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        refresh(),
        loadAssignedTasks(),
        selectedLead ? loadLeadCurrentStageDocuments(selectedLead) : Promise.resolve(),
        selectedCase ? loadCaseCurrentStageDocuments(selectedCase) : Promise.resolve(),
      ]);
    } finally {
      setRefreshing(false);
    }
  };

  const handleSendBulkReminders = async () => {
    if (!assistant?.firm_id) {
      setBulkReminderError("Assistant firm context is missing.");
      return;
    }

    setRunningBulkReminders(true);
    setBulkReminderError(null);
    setBulkReminderSuccess(null);

    try {
      const result = await assistantService.runDueReminders({
        firmId: assistant.firm_id,
        startedBy: assistant.id,
      });

      const summary = [
        `${result.triggered} triggered`,
        `${result.skipped} skipped`,
        `${result.failed} failed`,
        `${result.escalated} escalated`,
      ].join(", ");
      setBulkReminderSuccess(`Bulk reminders sent (${summary}).`);

      await Promise.all([
        loadAssignedTasks(),
        selectedLead ? loadLeadCurrentStageDocuments(selectedLead) : Promise.resolve(),
        selectedCase ? loadCaseCurrentStageDocuments(selectedCase) : Promise.resolve(),
      ]);
      await refresh();
    } catch (err) {
      setBulkReminderError(toErrorMessage(err, "Could not send bulk reminders."));
    } finally {
      setRunningBulkReminders(false);
    }
  };

  if (loading && !assistant) {
    return (
      <div className="app">
        <nav className="topnav">
          <div className="logo">
            <div className="logo-mark">M</div>
            CaseFlow
          </div>
          <div className="nav-right">
            <UserMenu />
          </div>
        </nav>
        <div className="page active">
          <div className="assistant-layout">
            <aside className="admin-sidebar" />
            <div className="admin-main">
              <section className="card">Loading assistant workspace...</section>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!assistant) {
    return (
      <div className="app">
        <nav className="topnav">
          <div className="logo">
            <div className="logo-mark">M</div>
            CaseFlow
          </div>
          <div className="nav-right">
            <UserMenu />
          </div>
        </nav>
        <div className="page active">
          <div className="assistant-layout">
            <aside className="admin-sidebar" />
            <div className="admin-main">
              <section className="notice notice-red">
                <i className="ti ti-alert-triangle" />
                <span>
                  {error ?? "Could not load assistant workspace data."}{" "}
                  <button
                    className="btn btn-sm"
                    onClick={() => {
                      void refresh();
                    }}
                  >
                    Retry
                  </button>
                </span>
              </section>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const initials = `${assistant.first_name[0] ?? ""}${assistant.last_name[0] ?? ""}`.toUpperCase();
  const reviewModalFile = reviewModalDocument ? latestDocumentFile(reviewModalDocument) : null;
  const isReviewActionInFlight = reviewModalDocument
    ? (reviewingDocumentIds[reviewModalDocument.document_item_id] ?? false)
    : false;

  return (
    <div className="app">
      <nav className="topnav">
        <div className="logo">
          <div className="logo-mark">M</div>
          CaseFlow
        </div>
        <div className="nav-right">
          <UserMenu initials={initials} />
        </div>
      </nav>

      <div className="page active">
        <div className="assistant-layout">
          <aside className="admin-sidebar">
            <div className="section-label" style={{ marginTop: 4 }}>
              Lead pipeline
            </div>
            {leads.length === 0 ? (
              <div className="notice notice-blue" style={{ marginBottom: 0 }}>
                <i className="ti ti-info-circle" />
                <span>No leads are assigned yet.</span>
              </div>
            ) : (
              leads.map((lead) => (
                <div
                  className={`case-row ${
                    activeSelection === "lead" && lead.lead_id === selectedLeadId
                      ? "selected"
                      : ""
                  }`}
                  style={{ cursor: "pointer" }}
                  role="button"
                  tabIndex={0}
                  key={lead.lead_id}
                  onClick={() => {
                    handleSelectLead(lead.lead_id);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleSelectLead(lead.lead_id);
                    }
                  }}
                >
                  <div className="avatar av-teal">
                    {`${lead.first_name[0] ?? ""}${lead.last_name[0] ?? ""}`.toUpperCase()}
                  </div>
                  <div className="case-info">
                    <div className="case-name">{fullName(lead.first_name, lead.last_name)}</div>
                    <div className="case-detail">
                      {lead.visa_type} · {lead.email}
                    </div>
                  </div>
                  <span className={`badge ${statusToBadgeClass(lead.status)}`}>
                    {lead.status.replaceAll("_", " ")}
                  </span>
                </div>
              ))
            )}
            {(leadPagination ? leadPagination.total > leadPagination.limit : leads.length > 5) ? (
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button
                  className="btn btn-sm"
                  onClick={prevLeadPage}
                  disabled={!leadPagination || leadPagination.offset === 0}
                >
                  Prev
                </button>
                <button
                  className="btn btn-sm"
                  onClick={nextLeadPage}
                  disabled={!leadPagination || !leadPagination.has_more}
                >
                  Next
                </button>
              </div>
            ) : null}
            {leadPagination ? (
              <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 4 }}>
                Showing {leadPagination.returned} of {leadPagination.total}
              </div>
            ) : null}

            <div className="divider" />
            <div className="section-label">Case load</div>
            {cases.length === 0 ? (
              <div className="notice notice-blue" style={{ marginBottom: 0 }}>
                <i className="ti ti-info-circle" />
                <span>No cases are assigned yet.</span>
              </div>
            ) : (
              cases.map((caseItem) => (
                <div
                  className={`case-row ${
                    activeSelection === "case" && caseItem.case_id === selectedCaseId
                      ? "selected"
                      : ""
                  }`}
                  style={{ cursor: "pointer" }}
                  role="button"
                  tabIndex={0}
                  key={caseItem.case_id}
                  onClick={() => {
                    handleSelectCase(caseItem.case_id);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleSelectCase(caseItem.case_id);
                    }
                  }}
                >
                  <div className="avatar av-blue">
                    {`${(caseItem.title || caseItem.case_number)[0] ?? "C"}`.toUpperCase()}
                  </div>
                  <div className="case-info">
                    <div className="case-name">{caseItem.title || caseItem.case_number}</div>
                    <div className="case-detail">
                      {caseItem.current_stage_label ?? toLabel(caseItem.lifecycle_status)} ·{" "}
                      {caseItem.case_number}
                    </div>
                  </div>
                  <span
                    className={`badge ${CASE_STAGE_BADGE_BY_STATUS[caseItem.stage_status] ?? "badge-gray"}`}
                  >
                    {toLabel(caseItem.stage_status)}
                  </span>
                </div>
              ))
            )}
            {(casePagination ? casePagination.total > casePagination.limit : cases.length > 5) ? (
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button
                  className="btn btn-sm"
                  onClick={prevCasePage}
                  disabled={!casePagination || casePagination.offset === 0}
                >
                  Prev
                </button>
                <button
                  className="btn btn-sm"
                  onClick={nextCasePage}
                  disabled={!casePagination || !casePagination.has_more}
                >
                  Next
                </button>
              </div>
            ) : null}
            {casePagination ? (
              <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 4 }}>
                Showing {casePagination.returned} of {casePagination.total}
              </div>
            ) : null}

            <div className="divider" />
            <div className="section-label">Quick actions</div>
            <div className="admin-actions">
              <button
                className="btn btn-sm"
                onClick={() => {
                  setLeadSubmitError(null);
                  setIsLeadModalOpen(true);
                }}
              >
                <i className="ti ti-user-plus" /> Add new lead
              </button>
              {/* <button
                className="btn btn-sm"
                onClick={() => {
                  scrollToSection(consultationSectionRef.current);
                }}
              >
                <i className="ti ti-calendar-event" /> Book consultation
              </button> */}
              <button
                className="btn btn-sm"
                disabled={runningBulkReminders}
                onClick={() => {
                  void handleSendBulkReminders();
                }}
              >
                <i className="ti ti-mail" />{" "}
                {runningBulkReminders ? "Sending reminders..." : "Send bulk reminders"}
              </button>
              <button className="btn btn-sm">
                <i className="ti ti-file-export" /> Export case list
              </button>
              <button
                className="btn btn-primary btn-sm"
                disabled={refreshing}
                onClick={() => {
                  void handleRefresh();
                }}
              >
                <i className="ti ti-refresh" />
                {refreshing ? "Refreshing..." : "Refresh data"}
              </button>
            </div>
            {bulkReminderError ? (
              <section className="notice notice-red" style={{ marginTop: 8, marginBottom: 0 }}>
                <i className="ti ti-alert-triangle" />
                <span>{bulkReminderError}</span>
              </section>
            ) : null}
            {bulkReminderSuccess ? (
              <section className="notice notice-green" style={{ marginTop: 8, marginBottom: 0 }}>
                <i className="ti ti-check" />
                <span>{bulkReminderSuccess}</span>
              </section>
            ) : null}
          </aside>

          <div className="admin-main">
            <div className="page-hd">
              <div className="page-title">Assistant workspace</div>
            </div>

            {error ? (
              <section className="notice notice-amber">
                <i className="ti ti-alert-circle" />
                <span>{error}</span>
              </section>
            ) : null}

            {activeSelection === "lead" ? (
              <section className="card" style={{ marginBottom: 16 }}>
                <div className="card-hd">
                  <i className="ti ti-road" /> Lead workflow
                </div>

                {!selectedLead ? (
                  <div className="notice notice-blue" style={{ marginBottom: 0 }}>
                    <i className="ti ti-info-circle" />
                    <span>Select a lead from the left panel to view workflow stages.</span>
                  </div>
                ) : noWorkflowStagesConfigured ? (
                  <div className="notice notice-blue" style={{ marginBottom: 0 }}>
                    <i className="ti ti-info-circle" />
                    <span>No stages are configured for this template.</span>
                  </div>
                ) : (
                  <>
                    <div className="journey-strip-full">
                      {stageSteps.map((step, index) => (
                        <div
                          key={step.id}
                          className={`jf-step ${step.state === "locked" ? "pending" : step.state}`}
                        >
                          <div className="jf-num">{index + 1}</div>
                          <div className="jf-lbl">{step.label}</div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </section>
            ) : null}

            {activeSelection === "lead" ? (
              <div className="g2">
              <div className="stack">
                <section className="card">
                  <div className="card-hd">
                    <i className="ti ti-user" /> Lead details
                  </div>

                  {!selectedLead ? (
                    <div className="notice notice-blue" style={{ marginBottom: 0 }}>
                      <i className="ti ti-info-circle" />
                      <span>Select a lead from the left panel to view lead details.</span>
                    </div>
                  ) : (
                    <div className="assistant-stage-detail">
                      <div className="assistant-stage-row">
                        Name: {fullName(selectedLead.first_name, selectedLead.last_name)}
                      </div>
                      <div className="assistant-stage-row">
                        Lead number: {selectedLead.lead_number}
                      </div>
                      <div className="assistant-stage-row">
                        Assigned advisor: {selectedLeadAdvisorName}
                      </div>
                      <div className="assistant-stage-row">
                        Lead status: {selectedLead.status.replaceAll("_", " ")}
                      </div>
                      <div className="assistant-stage-row">
                        Next stage:{" "}
                        {nextLeadStageLabel ??
                          (selectedLeadWorkflow?.currentStageLabel ? "No further stage" : "None")}
                      </div>

                      {showMoreLeadDetails ? (
                        <>
                          <div className="assistant-stage-row" style={{ marginTop: 8 }}>
                            Current stage:{" "}
                            {selectedLeadWorkflow?.currentStageLabel ??
                              selectedLead.status.replaceAll("_", " ")}
                          </div>
                          <div className="assistant-stage-row">Email: {selectedLead.email}</div>
                          <div className="assistant-stage-row">
                            Phone: {selectedLead.phone || "Not provided"}
                          </div>
                          <div className="assistant-stage-row">
                            Visa type: {selectedLead.visa_type}
                          </div>
                          <div className="assistant-stage-row">
                            Source: {selectedLead.source_channel || "Not provided"}
                          </div>
                          <div className="assistant-stage-row">
                            Consultation date: {formatDate(selectedLead.consultation_date)}
                          </div>
                          <div className="assistant-stage-row">
                            No-show follow-up outcome:{" "}
                            {selectedLead.followup_outcome
                              ? toLabel(selectedLead.followup_outcome)
                              : "Not recorded"}
                          </div>
                        </>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => {
                          setShowMoreLeadDetails((prev) => !prev);
                        }}
                        style={{
                          marginTop: 8,
                          padding: 0,
                          border: "none",
                          background: "transparent",
                          color: "var(--blue-600)",
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        {showMoreLeadDetails ? "Show less" : "Show more"}
                      </button>
                    </div>
                  )}
                </section>

                <section className="card">
                  <div className="card-hd">
                    <i className="ti ti-list-check" /> Current stage tasks
                  </div>

                  {!selectedLead ? (
                    <div className="notice notice-blue" style={{ marginBottom: 0 }}>
                      <i className="ti ti-info-circle" />
                      <span>Select a lead from the left panel to view current-stage tasks.</span>
                    </div>
                  ) : (
                    <>
                      {leadTasksLoading ? (
                        <div className="assistant-stage-row">Loading stage tasks...</div>
                      ) : null}
                      {leadTasksError ? (
                        <section className="notice notice-red" style={{ marginBottom: 0 }}>
                          <i className="ti ti-alert-triangle" />
                          <span>{leadTasksError}</span>
                        </section>
                      ) : null}
                      {!leadTasksLoading && !leadTasksError && leadStageTasks.length === 0 ? (
                        <div className="assistant-stage-row">
                          No tasks assigned for this stage.
                        </div>
                      ) : null}
                      {!leadTasksLoading && !leadTasksError
                        ? leadStageTasks.map((task) => {
                            const isDone = task.status === "DONE";
                            return (
                              <div className="task-item" key={task.task_id}>
                                <label className={`task-check ${isDone ? "done" : ""}`}>
                                  <input
                                    type="checkbox"
                                    className="task-checkbox-input"
                                    checked={isDone}
                                    disabled={updatingLeadTaskIds[task.task_id] ?? false}
                                    onChange={() => {
                                      void handleToggleLeadTask(task);
                                    }}
                                    aria-label={`Toggle stage task: ${task.title}`}
                                  />
                                  {isDone ? <i className="ti ti-check" style={{ fontSize: 11 }} /> : null}
                                </label>
                                <div className="task-body">
                                  <div className={`task-text ${isDone ? "done-text" : ""}`}>
                                    {task.title}
                                  </div>
                                  {task.description ? (
                                    <div className="task-sub">{task.description}</div>
                                  ) : null}
                                </div>
                              </div>
                            );
                          })
                        : null}
                    </>
                  )}
                </section>

                <section className="card">
                  <div className="card-hd">
                    <i className="ti ti-files" /> Documents
                  </div>

                  {!selectedLead ? (
                    <div className="notice notice-blue" style={{ marginBottom: 0 }}>
                      <i className="ti ti-info-circle" />
                      <span>Select a lead from the left panel to view current-stage documents.</span>
                    </div>
                  ) : leadDocumentsLoading ? (
                    <div className="assistant-stage-row">Loading stage documents...</div>
                  ) : leadDocumentsError ? (
                    <section className="notice notice-red" style={{ marginBottom: 0 }}>
                      <i className="ti ti-alert-triangle" />
                      <span>{leadDocumentsError}</span>
                    </section>
                  ) : currentStageDocuments.length === 0 ? (
                    <div className="assistant-stage-row">
                      No documents are configured for this stage.
                    </div>
                  ) : (
                    <>
                      <div className="assistant-stage-row" style={{ marginTop: 0 }}>
                        Current stage:{" "}
                        {currentStageDocumentsData?.current_stage_label ??
                          selectedLeadWorkflow?.currentStageLabel ??
                          "Unknown"}
                      </div>
                      <div className="advisor-documents-table-wrap">
                        <table className="advisor-documents-table">
                          <thead>
                            <tr>
                              <th>Document</th>
                              <th>Required</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentStageDocuments.map((document) => {
                              const latestFile = latestDocumentFile(document);
                              const canUpload = assistantCanUploadDocument(document);
                              const canDownload = Boolean(latestFile);
                              const canReview =
                                assistantCanReviewDocument(document) &&
                                Boolean(latestFile) &&
                                document.status !== "VERIFIED";
                              const canView = Boolean(latestFile) && document.status === "VERIFIED";

                              return (
                                <tr key={document.document_item_id}>
                                  <td>
                                    <div className="advisor-documents-label">{document.label}</div>
                                    {document.description ? (
                                      <div className="advisor-documents-description">
                                        {document.description}
                                      </div>
                                    ) : null}
                                  </td>
                                  <td>
                                    <span
                                      className={`badge ${document.is_required ? "badge-amber" : "badge-gray"}`}
                                    >
                                      {document.is_required ? "Required" : "Optional"}
                                    </span>
                                  </td>
                                  <td>
                                    <span className={`badge ${documentStatusBadgeClass(document.status)}`}>
                                      {documentStatusLabel(document.status)}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="advisor-documents-actions">
                                      {canUpload ? (
                                        <label
                                          className="btn btn-sm advisor-documents-action-btn"
                                          title={
                                            uploadingDocumentIds[document.document_item_id]
                                              ? "Uploading..."
                                              : "Upload"
                                          }
                                          aria-label={
                                            uploadingDocumentIds[document.document_item_id]
                                              ? "Uploading..."
                                              : "Upload"
                                          }
                                          style={{ position: "relative", overflow: "hidden" }}
                                        >
                                          <i
                                            className={
                                              uploadingDocumentIds[document.document_item_id]
                                                ? "ti ti-loader-2"
                                                : "ti ti-upload"
                                            }
                                          />
                                          <input
                                            type="file"
                                            style={{
                                              position: "absolute",
                                              inset: 0,
                                              opacity: 0,
                                              cursor: "pointer",
                                            }}
                                            disabled={uploadingDocumentIds[document.document_item_id]}
                                            onChange={(event) => {
                                              const selectedFile = event.target.files?.[0];
                                              if (selectedFile) {
                                                void handleUploadDocument(document, selectedFile);
                                              }
                                              event.currentTarget.value = "";
                                            }}
                                          />
                                        </label>
                                      ) : null}
                                      {canDownload && latestFile ? (
                                        <a
                                          className="btn btn-sm advisor-documents-action-btn"
                                          href={latestFile.blob_url}
                                          download={latestFile.file_name}
                                          target="_blank"
                                          rel="noreferrer"
                                          title="Download"
                                          aria-label="Download"
                                        >
                                          <i className="ti ti-download" />
                                        </a>
                                      ) : null}
                                      {canReview ? (
                                        <button
                                          className="btn btn-sm advisor-documents-action-btn"
                                          disabled={reviewingDocumentIds[document.document_item_id]}
                                          onClick={() => openDocumentReviewModal(document, "lead")}
                                          title="Review"
                                          aria-label="Review"
                                        >
                                          <i className="ti ti-search" />
                                        </button>
                                      ) : null}
                                      {canView ? (
                                        <button
                                          className="btn btn-sm advisor-documents-action-btn"
                                          onClick={() => openDocumentViewModal(document, "lead")}
                                          title="View"
                                          aria-label="View"
                                        >
                                          <i className="ti ti-eye" />
                                        </button>
                                      ) : null}
                                      {!canUpload && !canDownload && !canReview && !canView ? (
                                        <span className="advisor-documents-muted">No actions</span>
                                      ) : null}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </section>
              </div>

              <section className="card">
                <div className="card-hd">
                  <i className="ti ti-calendar-event" />{" "}
                  {shouldShowRebookingAction
                    ? "Consultation rebooking"
                    : shouldShowWrongNumberAction
                      ? "Update contact details"
                    : shouldShowInitialContactCapture
                      ? "Initial lead contact"
                    : isPreConsultationStage
                      ? "Initial lead contact"
                    : isConsultationStage
                      ? "Lead progressed"
                      : isEngagementStage
                        ? "Engagement letter"
                        : isEligibilityStage
                          ? "Eligibility in progress"
                          : "Lead progression"}
                </div>

                {!selectedLead ? (
                  <div className="notice notice-blue" style={{ marginBottom: 0 }}>
                    <i className="ti ti-info-circle" />
                    <span>Select a lead from the left panel to book a consultation.</span>
                  </div>
                ) : shouldShowInitialContactCapture ||
                  shouldShowRebookingAction ||
                  shouldShowWrongNumberAction ? (
                  <>
                    {bookingError ? (
                      <section className="notice notice-red">
                        <i className="ti ti-alert-triangle" />
                        <span>{bookingError}</span>
                      </section>
                    ) : null}
                    {shouldShowRebookingAction && hasBlockingOpenLeadStageTasksForBooking ? (
                      <section className="notice notice-amber">
                        <i className="ti ti-lock" />
                        <span>
                          Complete all your current-stage tasks before booking consultation.
                        </span>
                      </section>
                    ) : null}
                    {initialContactError ? (
                      <section className="notice notice-red">
                        <i className="ti ti-alert-triangle" />
                        <span>{initialContactError}</span>
                      </section>
                    ) : null}
                    {initialContactSuccess ? (
                      <section className="notice notice-green">
                        <i className="ti ti-check" />
                        <span>{initialContactSuccess}</span>
                      </section>
                    ) : null}
                    {leadPhoneError ? (
                      <section className="notice notice-red">
                        <i className="ti ti-alert-triangle" />
                        <span>{leadPhoneError}</span>
                      </section>
                    ) : null}
                    {leadPhoneSuccess ? (
                      <section className="notice notice-green">
                        <i className="ti ti-check" />
                        <span>{leadPhoneSuccess}</span>
                      </section>
                    ) : null}
                    {shouldShowRebookingAction ? (
                      <section className="notice notice-blue">
                        <i className="ti ti-info-circle" />
                        <span>
                          Follow-up/no-show task detected. Rebook consultation after client contact.
                        </span>
                      </section>
                    ) : null}
                    {shouldShowInitialContactCapture ? (
                      <>
                        <section className="notice notice-blue">
                          <i className="ti ti-phone-call" />
                          <span>
                            Record the first-call result for this Lead Capture Stage.
                          </span>
                        </section>
                        <div className="field">
                          <label>Contact outcome</label>
                          <select
                            value={initialContactOutcomeDraft}
                            onChange={(event) => {
                              handleInitialContactOutcomeDraftChange(event.target.value);
                            }}
                          >
                            <option value="">Select outcome</option>
                            <option value="CONTACTED">Contacted</option>
                            <option value="NO_ANSWER">No answer</option>
                            <option value="WRONG_NUMBER">Wrong number</option>
                          </select>
                        </div>
                        {initialContactOutcomeDraft === "CONTACTED" ||
                        initialContactOutcomeDraft === "NO_ANSWER" ? (
                          <div className="field">
                            <label>Call log date and time</label>
                            <input
                              type="datetime-local"
                              value={initialContactCallLoggedAt}
                              onChange={(event) => {
                                handleInitialContactCallLoggedAtChange(event.target.value);
                              }}
                            />
                          </div>
                        ) : null}
                        {initialContactOutcomeDraft === "WRONG_NUMBER" ? (
                          <div className="field">
                            <label>Correct phone number</label>
                            <input
                              type="text"
                              value={leadPhoneDraft}
                              onChange={(event) => {
                                handleLeadPhoneDraftChange(event.target.value);
                              }}
                              placeholder="Enter correct phone number"
                            />
                          </div>
                        ) : null}
                        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                          <button
                            className="btn btn-primary btn-sm"
                            disabled={savingInitialContactOutcome}
                            onClick={() => {
                              void handleSaveInitialContactOutcome();
                            }}
                          >
                            <i className="ti ti-device-mobile-check" />{" "}
                            {savingInitialContactOutcome
                              ? "Saving..."
                              : "Save contact outcome"}
                          </button>
                        </div>
                        <section className="notice notice-amber" style={{ marginTop: 12 }}>
                          <i className="ti ti-lock" />
                          <span>
                            {`Lead will automatically move to ${nextLeadStageLabel ?? "the next stage"} after outcome is saved as Contacted.`}
                          </span>
                        </section>
                      </>
                    ) : null}
                    {shouldShowWrongNumberAction ? (
                      <div className="field">
                        <label>Update phone number for lead</label>
                        <input
                          type="text"
                          value={leadPhoneDraft}
                          onChange={(e) => {
                            handleLeadPhoneDraftChange(e.target.value);
                          }}
                          placeholder="Enter updated phone number"
                        />
                        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                          <button
                            className="btn btn-sm"
                            disabled={updatingLeadPhone}
                            onClick={() => {
                              void handleUpdateLeadPhone();
                            }}
                          >
                            <i className="ti ti-device-mobile-check" />{" "}
                            {updatingLeadPhone ? "Updating..." : "Update phone"}
                          </button>
                        </div>
                      </div>
                    ) : null}

                    {shouldShowRebookingAction ? (
                      <>
                        <div className="field">
                          <label>Consultation date</label>
                          <input
                            type="date"
                            value={selectedLeadBooking?.consultationDate ?? ""}
                            onChange={(e) =>
                              handleBookingFieldChange(
                                selectedLead.lead_id,
                                "consultationDate",
                                e.target.value
                              )
                            }
                          />
                        </div>

                        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                          <button
                            className="btn btn-primary btn-sm"
                            disabled={
                              submittingBooking ||
                              savingInitialContactOutcome ||
                              leadTasksLoading ||
                              isUpdatingLeadTasks ||
                              hasBlockingOpenLeadStageTasksForBooking
                            }
                            onClick={() => {
                              void handleBookConsultation();
                            }}
                          >
                            <i className="ti ti-calendar-check" />
                            {submittingBooking
                              ? "Booking..."
                              : shouldShowRebookingAction
                                ? "Rebook consultation"
                                : "Book consultation"}
                          </button>
                        </div>
                      </>
                    ) : null}
                  </>
                ) : isConsultationStage ? (
                  <section className="notice notice-blue" style={{ marginBottom: 0 }}>
                    <i className="ti ti-calendar-time" />
                    <span>
                      {`Lead moved to ${selectedLeadWorkflow?.currentStageLabel ?? "the current stage"} after initial contact. Consultation booked for ${formatConsultationSchedule(selectedLead.consultation_date)}.`}
                    </span>
                  </section>
                ) : isEligibilityStage ? (
                  <>
                    <section className="notice notice-amber">
                      <i className="ti ti-hourglass-high" />
                      <span>
                        Advisor is finalizing eligibility details before engagement.
                      </span>
                    </section>
                  </>
                ) : isEngagementStage ? (
                  <>
                    {engagementError ? (
                      <section className="notice notice-red">
                        <i className="ti ti-alert-triangle" />
                        <span>{engagementError}</span>
                      </section>
                    ) : null}
                    {engagementSuccess ? (
                      <section className="notice notice-green">
                        <i className="ti ti-check" />
                        <span>{engagementSuccess}</span>
                      </section>
                    ) : null}
                    {conversionError ? (
                      <section className="notice notice-red">
                        <i className="ti ti-alert-triangle" />
                        <span>{conversionError}</span>
                      </section>
                    ) : null}
                    {conversionSuccess ? (
                      <section className="notice notice-green">
                        <i className="ti ti-check" />
                        <span>{conversionSuccess}</span>
                      </section>
                    ) : null}
                    {!hasUploadedEngagementLetter ? (
                      <section className="notice notice-amber">
                        <i className="ti ti-file-upload" />
                        <span>
                          Upload the engagement letter document in Current stage documents before sending
                          the engagement letter.
                        </span>
                      </section>
                    ) : null}

                    {hasUploadedEngagementLetter &&
                    !isEngagementLetterAdvisorApproved &&
                    !isEngagementLetterSent ? (
                      <section className="notice notice-amber">
                        <i className="ti ti-lock" />
                        <span>
                          Advisor has to approve the engagement letter document before it can be sent.
                        </span>
                      </section>
                    ) : null}

                    {hasUploadedEngagementLetter &&
                    isEngagementLetterAdvisorApproved &&
                    !isEngagementLetterSent ? (
                      <>
                        <section className="notice notice-blue">
                          <i className="ti ti-mail-forward" />
                          <span>
                            {selectedLeadStatusUpper === "BORDERLINE"
                              ? "Lead is borderline. Engagement letter can now be sent and flagged for adviser review."
                              : "Lead is eligible. Engagement letter can now be sent to move client onboarding forward."}
                          </span>
                        </section>
                        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                          <button
                            className="btn btn-primary btn-sm"
                            disabled={sendingEngagementLetter}
                            onClick={() => {
                              void handleSendEngagementLetter();
                            }}
                          >
                            <i className="ti ti-mail-forward" />{" "}
                            {sendingEngagementLetter ? "Sending..." : "Send engagement letter"}
                          </button>
                        </div>
                      </>
                    ) : null}

                    {isEngagementLetterSent ? (
                      <>
                        <section className="notice notice-blue">
                          <i className="ti ti-mail-check" />
                          <span>
                            Engagement letter sent. Confirm whether the client signed document is received.
                          </span>
                        </section>

                        <div className="field" style={{ marginTop: 12 }}>
                          <label>Client signed document status</label>
                          <select
                            value={clientSignedDocumentStatus}
                            onChange={(event) => {
                              const nextStatus = event.target.value as ClientSignedDocumentStatus;
                              setClientSignedDocumentStatus(nextStatus);
                              if (nextStatus === "RECEIVED" && !engagementSignedDocumentTask) {
                                void handleCreateSignedDocumentUploadTask("RECEIVED");
                              }
                            }}
                            disabled={Boolean(engagementSignedDocumentTask)}
                          >
                            <option value="PENDING">Select status</option>
                            <option value="RECEIVED">Received</option>
                            <option value="NOT_RECEIVED">Not received yet</option>
                          </select>
                        </div>

                        {clientSignedDocumentStatus === "NOT_RECEIVED" ? (
                          <section className="notice notice-amber">
                            <i className="ti ti-clock" />
                            <span>
                              Waiting for client signed document. Upload task will be created automatically once received is selected.
                            </span>
                          </section>
                        ) : null}

                        {creatingSignedDocumentTask ? (
                          <section className="notice notice-blue">
                            <i className="ti ti-loader-2" />
                            <span>Creating signed-document upload task...</span>
                          </section>
                        ) : null}

                        {signedDocumentTaskError ? (
                          <section className="notice notice-red">
                            <i className="ti ti-alert-triangle" />
                            <span>{signedDocumentTaskError}</span>
                          </section>
                        ) : null}
                        {signedDocumentTaskSuccess ? (
                          <section className="notice notice-green">
                            <i className="ti ti-check" />
                            <span>{signedDocumentTaskSuccess}</span>
                          </section>
                        ) : null}

                        {engagementSignedDocumentTask ? (
                          <section
                            className={`notice ${
                              isSignedDocumentTaskDone ? "notice-green" : "notice-amber"
                            }`}
                          >
                            <i
                              className={`ti ${
                                isSignedDocumentTaskDone ? "ti-check" : "ti-hourglass-high"
                              }`}
                            />
                            <span>
                              {isSignedDocumentTaskDone
                                ? "Signed engagement document upload task is complete."
                                : "Signed engagement document upload task is open. Mark it done after upload."}
                            </span>
                          </section>
                        ) : null}

                        {canConvertLeadToClient ? (
                          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                            <button
                              className="btn btn-primary btn-sm"
                              disabled={convertingLead}
                              onClick={() => {
                                void handleConvertLeadToClient();
                              }}
                            >
                              <i className="ti ti-user-check" />{" "}
                              {convertingLead ? "Converting..." : "Create client from lead"}
                            </button>
                          </div>
                        ) : null}
                      </>
                    ) : null}
                  </>
                ) : (
                  <section className="notice notice-blue" style={{ marginBottom: 0 }}>
                    <i className="ti ti-info-circle" />
                    <span>
                      No consultation action required for current status:{" "}
                      <strong>{selectedLead.status.replaceAll("_", " ")}</strong>.
                    </span>
                  </section>
                )}
              </section>
            </div>
            ) : (
              <>
              <section className="card" style={{ marginBottom: 16 }}>
                <div className="card-hd">
                  <i className="ti ti-road" /> Case workflow
                </div>
                {!selectedCase ? (
                  <div className="notice notice-blue" style={{ marginBottom: 0 }}>
                    <i className="ti ti-info-circle" />
                    <span>Select a case from the left panel to view workflow stages.</span>
                  </div>
                ) : (
                  <div className="journey-strip-full">
                    {caseStageSteps.map((step, index) => (
                      <div
                        key={step.id}
                        className={`jf-step ${step.state === "locked" ? "pending" : step.state}`}
                      >
                        <div className="jf-num">{index + 1}</div>
                        <div className="jf-lbl">{step.label}</div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
              <div className="g2">
                <div className="stack">
                  <section className="card">
                    <div className="card-hd">
                      <i className="ti ti-folder" /> Case details
                    </div>
                    {!selectedCase ? (
                      <div className="notice notice-blue" style={{ marginBottom: 0 }}>
                        <i className="ti ti-info-circle" />
                        <span>Select a case from the left panel to view case details.</span>
                      </div>
                    ) : (
                      <div className="assistant-stage-detail">
                        <div className="assistant-stage-row">
                          Title: {selectedCase.title || selectedCase.case_number}
                        </div>
                        <div className="assistant-stage-row">Case number: {selectedCase.case_number}</div>
                        <div className="assistant-stage-row">
                          Lifecycle: {toLabel(selectedCase.lifecycle_status)}
                        </div>
                        <div className="assistant-stage-row">
                          Stage status: {toLabel(selectedCase.stage_status)}
                        </div>
                        <div className="assistant-stage-row">
                          Current stage:{" "}
                          {selectedCase.current_stage_label ?? toLabel(selectedCase.lifecycle_status)}
                        </div>
                        {showMoreCaseDetails ? (
                          <>
                            <div className="assistant-stage-row" style={{ marginTop: 8 }}>
                              Visa type: {selectedCase.visa_type}
                            </div>
                            <div className="assistant-stage-row">
                              Country: {selectedCase.country_code}
                            </div>
                            <div className="assistant-stage-row">
                              Assigned advisor: {selectedCaseAdvisorName}
                            </div>
                            <div className="assistant-stage-row">
                              Assigned assistant: {selectedCaseAssistantLabel}
                            </div>
                            <div className="assistant-stage-row">
                              Opened: {formatDate(selectedCase.opened_at)}
                            </div>
                          </>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => {
                            setShowMoreCaseDetails((prev) => !prev);
                          }}
                          style={{
                            marginTop: 8,
                            padding: 0,
                            border: "none",
                            background: "transparent",
                            color: "var(--blue-600)",
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: "pointer",
                          }}
                        >
                          {showMoreCaseDetails ? "Show less" : "Show more"}
                        </button>
                      </div>
                    )}
                  </section>

                  <section className="card">
                    <div className="card-hd">
                      <i className="ti ti-list-check" /> Current stage tasks
                    </div>
                    {!selectedCase ? (
                      <div className="notice notice-blue" style={{ marginBottom: 0 }}>
                        <i className="ti ti-info-circle" />
                        <span>Select a case from the left panel to view current-stage tasks.</span>
                      </div>
                    ) : (
                      <>
                        {caseTasksLoading ? (
                          <div className="assistant-stage-row">Loading stage tasks...</div>
                        ) : null}
                        {caseTasksError ? (
                          <section className="notice notice-red" style={{ marginBottom: 0 }}>
                            <i className="ti ti-alert-triangle" />
                            <span>{caseTasksError}</span>
                          </section>
                        ) : null}
                        {!caseTasksLoading && !caseTasksError && caseStageTasks.length === 0 ? (
                          <div className="assistant-stage-row">
                            No case tasks assigned for this stage.
                          </div>
                        ) : null}
                        {!caseTasksLoading && !caseTasksError
                          ? caseStageTasks.map((task) => {
                              const isDone = task.status === "DONE";
                              return (
                                <div className="task-item" key={task.task_id}>
                                  <label className={`task-check ${isDone ? "done" : ""}`}>
                                    <input
                                      type="checkbox"
                                      className="task-checkbox-input"
                                      checked={isDone}
                                      disabled={updatingCaseTaskIds[task.task_id] ?? false}
                                      onChange={() => {
                                        void handleToggleCaseTask(task);
                                      }}
                                      aria-label={`Toggle case stage task: ${task.title}`}
                                    />
                                    {isDone ? <i className="ti ti-check" style={{ fontSize: 11 }} /> : null}
                                  </label>
                                  <div className="task-body">
                                    <div className={`task-text ${isDone ? "done-text" : ""}`}>
                                      {task.title}
                                    </div>
                                    {task.description ? (
                                      <div className="task-sub">{task.description}</div>
                                    ) : null}
                                  </div>
                                </div>
                              );
                            })
                          : null}
                      </>
                    )}
                  </section>

                  <section className="card">
                    <div className="card-hd">
                      <i className="ti ti-files" /> Documents
                    </div>
                    {!selectedCase ? (
                      <div className="notice notice-blue" style={{ marginBottom: 0 }}>
                        <i className="ti ti-info-circle" />
                        <span>Select a case from the left panel to view current-stage documents.</span>
                      </div>
                    ) : caseDocumentsLoading ? (
                      <div className="assistant-stage-row">Loading stage documents...</div>
                    ) : caseDocumentsError ? (
                      <section className="notice notice-red" style={{ marginBottom: 0 }}>
                        <i className="ti ti-alert-triangle" />
                        <span>{caseDocumentsError}</span>
                      </section>
                    ) : caseCurrentStageDocuments.length === 0 ? (
                      <div className="assistant-stage-row">
                        No documents are configured for this case stage.
                      </div>
                    ) : (
                      <>
                        <div className="assistant-stage-row" style={{ marginTop: 0 }}>
                          Current stage:{" "}
                          {caseCurrentStageDocumentsData?.current_stage_label ??
                            selectedCase.current_stage_label ??
                            toLabel(selectedCase.lifecycle_status)}
                        </div>
                        <div className="advisor-documents-table-wrap">
                          <table className="advisor-documents-table">
                            <thead>
                              <tr>
                                <th>Document</th>
                                <th>Required</th>
                                <th>Status</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {caseCurrentStageDocuments.map((document) => {
                                const latestFile = latestDocumentFile(document);
                                const canDownload = Boolean(latestFile);
                                const canReview =
                                  assistantCanReviewDocument(document) &&
                                  Boolean(latestFile) &&
                                  document.status !== "VERIFIED";
                                const canView = Boolean(latestFile);

                                return (
                                  <tr key={document.document_item_id}>
                                    <td>
                                      <div className="advisor-documents-label">{document.label}</div>
                                      {document.description ? (
                                        <div className="advisor-documents-description">
                                          {document.description}
                                        </div>
                                      ) : null}
                                    </td>
                                    <td>
                                      <span
                                        className={`badge ${document.is_required ? "badge-amber" : "badge-gray"}`}
                                      >
                                        {document.is_required ? "Required" : "Optional"}
                                      </span>
                                    </td>
                                    <td>
                                      <span className={`badge ${documentStatusBadgeClass(document.status)}`}>
                                        {documentStatusLabel(document.status)}
                                      </span>
                                    </td>
                                    <td>
                                      <div className="advisor-documents-actions">
                                        {canDownload && latestFile ? (
                                          <a
                                            className="btn btn-sm advisor-documents-action-btn"
                                            href={latestFile.blob_url}
                                            download={latestFile.file_name}
                                            target="_blank"
                                            rel="noreferrer"
                                            title="Download"
                                            aria-label="Download"
                                          >
                                            <i className="ti ti-download" />
                                          </a>
                                        ) : null}
                                        {canReview ? (
                                          <button
                                            className="btn btn-sm advisor-documents-action-btn"
                                            disabled={reviewingDocumentIds[document.document_item_id]}
                                            onClick={() => openDocumentReviewModal(document, "case")}
                                            title="Review"
                                            aria-label="Review"
                                          >
                                            <i className="ti ti-search" />
                                          </button>
                                        ) : null}
                                        {canView && latestFile ? (
                                          <button
                                            type="button"
                                            className="btn btn-sm advisor-documents-action-btn"
                                            onClick={() => openDocumentViewModal(document, "case")}
                                            title="View"
                                            aria-label="View"
                                          >
                                            <i className="ti ti-eye" />
                                          </button>
                                        ) : null}
                                        {!canDownload && !canReview && !canView ? (
                                          <span className="advisor-documents-muted">No actions</span>
                                        ) : null}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </section>
                </div>

                <section className="card">
                  <div className="card-hd">
                    <i className="ti ti-bulb" /> Next case actions
                  </div>
                  {!selectedCase ? (
                    <div className="notice notice-blue" style={{ marginBottom: 0 }}>
                      <i className="ti ti-info-circle" />
                      <span>Select a case from the left panel to view action guidance.</span>
                    </div>
                  ) : (
                    <>
                      {caseTransitionError ? (
                        <section className="notice notice-red">
                          <i className="ti ti-alert-triangle" />
                          <span>{caseTransitionError}</span>
                        </section>
                      ) : null}
                      {caseTransitionSuccess ? (
                        <section className="notice notice-green">
                          <i className="ti ti-check" />
                          <span>{caseTransitionSuccess}</span>
                        </section>
                      ) : null}
                      <section className="notice notice-blue">
                        <i className="ti ti-bulb" />
                        <span>
                          <strong>Next action:</strong> {caseAction?.title}. {caseAction?.detail}
                        </span>
                      </section>
                      {selectedCaseStatus === "BLOCKED" ? (
                        <section className="notice notice-red">
                          <i className="ti ti-alert-triangle" />
                          <span>
                            Case is currently blocked. Resolve blockers and complete pending tasks.
                          </span>
                        </section>
                      ) : null}
                      {/* {selectedCaseOpenTasks.length > 0 ? (
                        <section className="notice notice-amber">
                          <i className="ti ti-checklist" />
                          <span>
                            {selectedCaseOpenTasks.length} open stage task
                            {selectedCaseOpenTasks.length === 1 ? "" : "s"} assigned to you.
                            {nextCaseDueTask
                              ? ` Next due: ${nextCaseDueTask.title} (${formatDateTime(nextCaseDueTask.due_at)}).`
                              : ""}
                          </span>
                        </section>
                      ) : (
                        <section className="notice notice-green">
                          <i className="ti ti-check" />
                          <span>No open stage tasks assigned to you for this case.</span>
                        </section>
                      )} */}
                      <section className="notice notice-blue" style={{ marginBottom: 0 }}>
                        <i className="ti ti-file-check" />
                        <span>
                          Current-stage documents: {caseCurrentStageDocuments.length}. Review items in
                          the center panel.
                        </span>
                      </section>
                      {hasNextCaseStage ? (
                        <>
                          {!canAdvanceCaseStage ? (
                            <section className="notice notice-amber">
                              <i className="ti ti-lock" />
                              <span>
                                Complete all current-stage checks (required blocking docs verified
                                and all stage tasks done) before moving to the next stage.
                              </span>
                            </section>
                          ) : null}
                          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                            <button
                              className="btn btn-primary btn-sm"
                              disabled={
                                transitioningCaseStage ||
                                caseTasksLoading ||
                                caseDocumentsLoading ||
                                isUpdatingCaseTasks
                              }
                              onClick={() => {
                                void handleAdvanceCaseStage();
                              }}
                            >
                              <i className="ti ti-arrow-big-right-lines" />{" "}
                              {transitioningCaseStage ? "Moving..." : advanceCaseButtonLabel}
                            </button>
                          </div>
                        </>
                      ) : null}
                    </>
                  )}
                </section>
              </div>
              </>
            )}
          </div>

          <aside className="adv-right-panel">
            <section className="adv-section">
              <div className="section-label">Tasks Queue</div>
              {assignedTasksLoading ? (
                <div style={{ fontSize: 12, color: "var(--text-3)" }}>Loading tasks...</div>
              ) : null}
              {assignedTasksError ? (
                <section className="notice notice-red" style={{ marginBottom: 0 }}>
                  <i className="ti ti-alert-triangle" />
                  <span>{assignedTasksError}</span>
                </section>
              ) : null}
              {!assignedTasksLoading && !assignedTasksError && orderedAssignedTasks.length === 0 ? (
                <div style={{ fontSize: 12, color: "var(--text-3)" }}>
                  No open tasks assigned to you.
                </div>
              ) : null}
              {!assignedTasksLoading && !assignedTasksError
                ? orderedAssignedTasks.map((task) => {
                    const dueTimestamp = task.due_at ? new Date(task.due_at).getTime() : NaN;
                    const isOverdue =
                      Number.isFinite(dueTimestamp) && dueTimestamp < Date.now();
                    const isDone = task.status === "DONE";
                    const isUpdating = updatingAssignedTaskIds[task.task_id] ?? false;
                    const taskFullName = resolveAssignedTaskFullName(task, leadNameById);
                    const taskTitle = taskFullName ? `${task.title} - ${taskFullName}` : task.title;

                    return (
                      <div className="task-item" key={task.task_id}>
                        <label className={`task-check ${isDone ? "done" : isOverdue ? "overdue" : ""}`}>
                          <input
                            type="checkbox"
                            className="task-checkbox-input"
                            checked={isDone}
                            disabled={isUpdating}
                            onChange={() => {
                              void handleToggleAssignedTask(task);
                            }}
                            aria-label={`Toggle task: ${taskTitle}`}
                          />
                          {isDone ? <i className="ti ti-check" style={{ fontSize: 11 }} /> : null}
                        </label>
                        <div className="task-body">
                          <div className={`task-text ${isDone ? "done-text" : ""}`}>{taskTitle}</div>
                          {task.description ? (
                            <div className="task-sub">{task.description}</div>
                          ) : null}
                          <div className={`task-sub ${isOverdue ? "overdue" : ""}`}>
                            <span className={`badge ${taskSourceBadgeClass(task.source)}`}>
                              {task.source}
                            </span>{" "}
                            {isUpdating
                              ? "Updating..."
                              : `${isOverdue ? "Overdue" : "Due"} · ${formatDateTime(task.due_at)}`}
                          </div>
                        </div>
                      </div>
                    );
                  })
                : null}
            </section>
          </aside>
        </div>
      </div>

      {reviewModalDocument ? (
        <div
          className="assistant-modal-overlay"
          onClick={() => {
            if (!isReviewActionInFlight) {
              setReviewModalDocument(null);
              setReviewModalError(null);
            }
          }}
        >
          <section
            className="assistant-modal card assistant-document-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Review uploaded document"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <div className="assistant-modal-header">
              <div className="card-hd" style={{ marginBottom: 0 }}>
                <i className="ti ti-file-search" />{" "}
                {reviewModalMode === "view" ? "View document" : "Review document"}
              </div>
              <button
                className="btn btn-sm"
                disabled={isReviewActionInFlight}
                onClick={() => {
                  setReviewModalDocument(null);
                  setReviewModalError(null);
                }}
              >
                <i className="ti ti-x" /> Close
              </button>
            </div>

            <div className="assistant-stage-row" style={{ marginTop: 10 }}>
              {reviewModalDocument.label}
            </div>

            {reviewModalError ? (
              <section className="notice notice-red" style={{ marginTop: 12 }}>
                <i className="ti ti-alert-triangle" />
                <span>{reviewModalError}</span>
              </section>
            ) : null}

            <div className="assistant-document-preview">
              {reviewModalFile ? (
                reviewModalFile.content_type.startsWith("image/") ? (
                  <img src={reviewModalFile.blob_url} alt={reviewModalFile.file_name} />
                ) : (
                  <iframe
                    title={`Preview ${reviewModalFile.file_name}`}
                    src={reviewModalFile.blob_url}
                  />
                )
              ) : (
                <div className="assistant-stage-row">No uploaded file available for preview.</div>
              )}
            </div>

            {reviewModalFile ? (
              <div className="assistant-stage-row" style={{ marginTop: 8 }}>
                File: {reviewModalFile.file_name} ({Math.round(reviewModalFile.size_bytes / 1024)} KB)
              </div>
            ) : null}

            {reviewModalMode === "review" ? (
              <div className="field" style={{ marginTop: 12 }}>
                <label>Rejection reason (only used when rejecting)</label>
                <textarea
                  rows={3}
                  value={reviewRejectReason}
                  disabled={isReviewActionInFlight}
                  onChange={(event) => {
                    setReviewRejectReason(event.target.value);
                  }}
                  placeholder="Optional reason for rejection."
                />
              </div>
            ) : null}

            <div className="assistant-modal-actions">
              <button
                className="btn btn-sm"
                disabled={isReviewActionInFlight}
                onClick={() => {
                  setReviewModalDocument(null);
                  setReviewModalError(null);
                }}
              >
                Cancel
              </button>
              {reviewModalMode === "review" ? (
                <>
                  <button
                    className="btn btn-danger btn-sm"
                    disabled={isReviewActionInFlight || !reviewModalFile}
                    onClick={() => {
                      void handleReviewAction("REJECT");
                    }}
                  >
                    <i className="ti ti-x" /> {isReviewActionInFlight ? "Saving..." : "Reject"}
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={isReviewActionInFlight || !reviewModalFile}
                    onClick={() => {
                      void handleReviewAction("VERIFY");
                    }}
                  >
                    <i className="ti ti-check" /> {isReviewActionInFlight ? "Saving..." : "Review"}
                  </button>
                </>
              ) : null}
            </div>
          </section>
        </div>
      ) : null}

      {isLeadModalOpen ? (
        <div
          className="assistant-modal-overlay"
          onClick={() => {
            if (!submittingLead) {
              setIsLeadModalOpen(false);
            }
          }}
        >
          <section
            className="assistant-modal card"
            role="dialog"
            aria-modal="true"
            aria-label="Add new lead"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <div className="assistant-modal-header">
              <div className="card-hd" style={{ marginBottom: 0 }}>
                <i className="ti ti-user-plus" /> Add new lead
              </div>
              <button
                className="btn btn-sm"
                disabled={submittingLead}
                onClick={() => {
                  setIsLeadModalOpen(false);
                }}
              >
                <i className="ti ti-x" /> Close
              </button>
            </div>

            {leadSubmitError ? (
              <section className="notice notice-red" style={{ marginTop: 12 }}>
                <i className="ti ti-alert-triangle" />
                <span>{leadSubmitError}</span>
              </section>
            ) : null}

            <div className="field-group" style={{ marginTop: 12 }}>
              <div className="field">
                <label>First name</label>
                <input
                  type="text"
                  value={leadForm.first_name}
                  onChange={(e) => handleLeadFormChange("first_name", e.target.value)}
                />
              </div>
              <div className="field">
                <label>Last name</label>
                <input
                  type="text"
                  value={leadForm.last_name}
                  onChange={(e) => handleLeadFormChange("last_name", e.target.value)}
                />
              </div>
            </div>

            <div className="field-group">
              <div className="field">
                <label>Email</label>
                <input
                  type="email"
                  value={leadForm.email}
                  className={createLeadEmailInvalid ? "input-invalid" : undefined}
                  aria-invalid={createLeadEmailInvalid}
                  onChange={(e) => handleLeadFormChange("email", e.target.value)}
                />
                {createLeadEmailInvalid ? (
                  <div className="field-inline-error">Enter a valid email address.</div>
                ) : null}
              </div>
              <div className="field">
                <label>Phone</label>
                <input
                  type="text"
                  value={leadForm.phone}
                  onChange={(e) => handleLeadFormChange("phone", e.target.value)}
                />
              </div>
            </div>

            <div className="field-group">
              <div className="field">
                <label>Visa type / workflow template</label>
                <select
                  value={leadForm.template_id}
                  onChange={(e) => handleLeadTemplateChange(e.target.value)}
                  disabled={workflowTemplatesLoading}
                >
                  <option value="">
                    {workflowTemplatesLoading
                      ? "Loading templates..."
                      : availableWorkflowTemplates.length > 0
                        ? "Select workflow template"
                        : "No workflow templates available"}
                  </option>
                  {availableWorkflowTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {workflowTemplateLabel(template)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Country code</label>
                <input
                  type="text"
                  value={leadForm.country_code}
                  maxLength={2}
                  onChange={(e) => handleLeadCountryCodeChange(e.target.value)}
                />
              </div>
            </div>

            {workflowTemplatesError ? (
              <section className="notice notice-red" style={{ marginTop: 4 }}>
                <i className="ti ti-alert-triangle" />
                <span>{workflowTemplatesError}</span>
              </section>
            ) : null}

            <div className="field-group">
              <div className="field">
                <label>Source channel</label>
                <select
                  value={leadForm.source_channel}
                  onChange={(e) => handleLeadFormChange("source_channel", e.target.value)}
                >
                  {SOURCE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Assigned advisor</label>
                <select
                  value={leadForm.assigned_advisor_id}
                  onChange={(e) => handleLeadFormChange("assigned_advisor_id", e.target.value)}
                >
                  {advisors.map((advisor) => (
                    <option key={advisor.id} value={advisor.id}>
                      {fullName(advisor.first_name, advisor.last_name)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="field-group">
              <div className="field">
                <label>Enquiry subject</label>
                <input
                  type="text"
                  value={leadForm.enquiry_subject}
                  onChange={(e) => handleLeadFormChange("enquiry_subject", e.target.value)}
                />
              </div>
            </div>

            <div className="field">
              <label>Notes</label>
              <textarea
                rows={3}
                value={leadForm.notes}
                onChange={(e) => handleLeadFormChange("notes", e.target.value)}
              />
            </div>

            <div className="assistant-modal-actions">
              <button
                className="btn btn-sm"
                disabled={submittingLead}
                onClick={() => {
                  setIsLeadModalOpen(false);
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary btn-sm"
                disabled={isCreateLeadDisabled}
                onClick={() => {
                  void handleCreateLead();
                }}
              >
                <i className="ti ti-plus" />
                {submittingLead ? "Creating..." : "Create lead"}
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}
